<template>
  <div class="mfg-app container-fluid px-4 py-4" :class="{ 'mfg-detail-open': !!activeOrderId }">

    <!-- ── Bottom nav bar (PWA style) ──────────────────────────────────── -->
    <!-- <nav class="mfg-nav">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="mfg-nav-btn"
        :class="{ active: currentTab === tab.key }"
        @click="currentTab = tab.key"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
      </button>
    </nav> -->

    <!-- ── Main content area ────────────────────────────────────────────── -->
    <div class="mfg-content">
      <!-- Header -->
      <div class="mfg-header">
        <div>
          <h4 class="mfg-title">Manufacturing</h4>
          <p class="mfg-subtitle">{{ today }}</p>
        </div>
        <div class="d-flex" style="gap:8px; align-items:center;">
          <button class="btn btn-sm btn-primary" @click="openModal('newOrder')">
            <i class="bi bi-plus-lg mr-1"></i>New Order
          </button>
          <div class="dropdown">
            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
             More <i class="bi bi-three-dots-vertical"></i>
            </button>
            <div class="dropdown-menu dropdown-menu-right">
              <template v-if="canEditRecipes">
              <button class="dropdown-item" @click="openModal('newBom')">
                <i class="bi bi-file-earmark-plus mr-2"></i>New BOM
              </button>
              <div class="dropdown-divider"></div>
              </template>
              <button class="dropdown-item" @click="openModal('analytics')">
                <i class="bi bi-bar-chart-line mr-2"></i>Analytics
              </button>
              <button class="dropdown-item" @click="openModal('wastageLog')">
                <i class="bi bi-trash mr-2"></i>Wastage Log
              </button>
              <button class="dropdown-item" @click="openModal('stockLedger')">
                <i class="bi bi-journal-text mr-2"></i>Stock Ledger
              </button>
              <button class="dropdown-item" @click="$refs.repackModal.open()">
                <i class="bi bi-box-seam mr-2"></i>Pack produced stock
              </button>
              <button class="dropdown-item" @click="openPackingHistory">
                <i class="bi bi-clock-history mr-2"></i>Packing history
              </button>
              <div class="dropdown-divider"></div>
              <button class="dropdown-item" @click="openModal('lowStock')">
                <i class="bi bi-exclamation-triangle mr-2"></i>Low Stock
                <span v-if="kpis.low_stock_count > 0" class="badge badge-secondary ml-1">{{ kpis.low_stock_count }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-end mb-4" style="gap:12px;">
        <div class="mfg-tabs flex-grow-1" style="min-width:0;">
          <a v-for="tab in visibleTabs" :key="tab.key" href="#" class="mfg-tab" :class="{ active: currentTab === tab.key }" @click.prevent="currentTab = tab.key">{{ tab.label }}</a>
        </div>
        <button type="button" class="mfg-guide-btn" v-b-tooltip.hover.bottomleft title="Manufacturing guide" aria-label="Manufacturing guide" @click="$refs.guide.show()">
          <i class="bi bi-info-circle"></i>
        </button>
      </div>
      <manufacturing-guide ref="guide" :hidden-actions="sampleData.available && sampleKindsLeft.length ? [] : ['sampleData']" @action="onGuideAction"></manufacturing-guide>
      <b-modal ref="sampleModal" title="Load sample data" @ok.prevent="loadSampleData" ok-title="Load sample data" :ok-disabled="sampleData.busy || !sampleKindsLeft.includes(sampleData.kind)">
        <div class="btn-group btn-group-toggle d-flex mb-3">
          <button type="button" class="btn w-50" :class="sampleData.kind === 'feed' ? 'btn-primary' : 'btn-outline-primary'" :disabled="sampleData.kinds.feed" @click="sampleData.kind = 'feed'">Feed mill<span v-if="sampleData.kinds.feed"> (loaded)</span></button>
          <button type="button" class="btn w-50" :class="sampleData.kind === 'bakery' ? 'btn-primary' : 'btn-outline-primary'" :disabled="sampleData.kinds.bakery" @click="sampleData.kind = 'bakery'">Bakery<span v-if="sampleData.kinds.bakery"> (loaded)</span></button>
        </div>
        <p v-if="sampleData.kind === 'bakery'" class="mb-2">This adds a bakery to <strong>this business</strong>: a purchase of flour and other materials, a dough recipe and a bread recipe (with proofing and baking stages that use no materials), a dough batch, a bread batch made from that dough, and a counter sale by the tray. No lots or quality checks — those are optional.</p>
        <p v-else class="mb-2">This adds a complete feed-mill example to <strong>this business</strong>: purchases, quality checks, a recipe, a production batch, a cash sale, an invoice delivered to Kericho, and the payments.</p>
        <p class="text-secondary mb-0" style="font-size:13px;">Everything added is labelled “(sample)”. Your existing products and sales are not changed. It can only be loaded once.</p>
        <div v-if="sampleData.busy" class="mt-3 text-secondary"><span class="spinner-border spinner-border-sm mr-2"></span>Loading — this takes a few seconds…</div>
        <div v-if="sampleData.error" class="alert alert-danger mt-3 mb-0">{{ sampleData.error }}</div>
      </b-modal>

      <!-- ▌TAB: Dashboard ▌ -->
      <div v-show="currentTab === 'dashboard'">
        <!-- KPI strip -->
        <div class="row mb-4 pb-2 mfg-kpis" v-if="!dashLoading">
          <div class="col-6 col-md-3 kpi-col" v-for="k in kpiCards" :key="k.label" :style="k.action ? 'cursor:pointer' : ''" @click="k.action && k.action()">
            <div class="text-secondary mb-1" style="font-size:12px;">{{ k.label }}</div>
            <div class="font-weight-bold" :class="k.color" style="font-size:17px;">{{ k.value }}</div>
          </div>
        </div>

        <!-- <div class="c" v-if="!dashLoading">
          <div class="kpi-card">

          </div>
        </div>
        <div v-else class="kpi-strip">
          <div class="kpi-card skeleton" v-for="i in 5" :key="i"></div>
        </div> -->

        <!-- Low stock alert -->
        <div v-if="kpis.low_stock_count > 0" class="alert-banner" @click="openModal('lowStock')">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span><strong>{{ kpis.low_stock_count }}</strong> raw material{{ kpis.low_stock_count > 1 ? 's' : '' }} below minimum stock</span>
          <i class="bi bi-chevron-right ml-auto"></i>
        </div>

        <!-- Abnormal wastage alert -->
        <div v-if="kpis.abnormal_wastage_count > 0" class="alert-banner alert-banner--danger" @click="openModal('wastageLog')">
          <i class="bi bi-fire"></i>
          <span><strong>{{ kpis.abnormal_wastage_count }}</strong> abnormal wastage incident{{ kpis.abnormal_wastage_count > 1 ? 's' : '' }} this period</span>
          <i class="bi bi-chevron-right ml-auto"></i>
        </div>

        <!-- Active orders -->
        <div class="section-header">
          <h6 class="section-title">Active Orders</h6>
          <button class="btn-link-sm" @click="currentTab = 'orders'">View all</button>
        </div>

        <div v-if="activeOrders.length === 0 && !dashLoading" class="empty-state">
          <i class="bi bi-inbox" style="font-size:2rem; opacity:.3;"></i>
          <p class="text-muted mt-2 mb-0">No active orders</p>
          <button class="btn btn-sm btn-outline-primary mt-2" @click="openModal('newOrder')">Create first order</button>
        </div>

        <div class="order-cards mfg-box">
          <div
            class="order-card"
            v-for="order in activeOrders"
            :key="order.id"
            @click="openOrderDetail(order.id)"
          >
            <div class="order-card-top">
              <div>
                <span class="order-number">{{ order.order_number }}</span>
                <span :class="statusClass(order.status)" class="status-pill">{{ orderStatusLabel(order) }}</span>
              </div>
              <span class="order-time">{{ relativeTime(order.created_at) }}</span>
            </div>
            <div class="order-product">{{ order.output_product && order.output_product.product }}</div>
            <div class="order-card-bottom">
              <span class="order-meta">
                <i class="bi bi-box"></i> {{ order.planned_batch_size }} {{ order.bom && order.bom.batch_unit }}
              </span>
              <div class="order-progress" v-if="order.status === 'in_progress' && order.stages">
                <div class="progress-dots">
                  <span
                    v-for="(stage, i) in order.stages.slice(0, 6)"
                    :key="i"
                    class="progress-dot"
                    :class="stage.status"
                  ></span>
                </div>
              </div>
              <i class="bi bi-chevron-right text-muted"></i>
            </div>
          </div>
        </div>

        <!-- Recent completions mini-chart -->
        <div class="section-header mt-3">
          <h6 class="section-title">Recent Batches</h6>
          <button class="btn-link-sm" @click="openModal('analytics')">Full report</button>
        </div>
        <div class="batch-list" v-if="costTrend.length">
          <div class="batch-row" v-for="b in costTrend.slice(0, 5)" :key="b.order_number">
            <div class="batch-bar-wrap">
              <div class="batch-bar" :style="{ width: batchBarPct(b) + '%' }" :class="b.efficiency >= 90 ? 'bar-good' : b.efficiency >= 75 ? 'bar-warn' : 'bar-bad'"></div>
            </div>
            <div class="batch-info">
              <span class="batch-name">{{ b.product }}</span>
              <span class="batch-cost">{{ formatCurrency(b.actual_cost) }}</span>
              <span class="batch-eff" :class="b.efficiency >= 90 ? 'text-dark' : 'text-danger'">{{ b.efficiency }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ▌TAB: Orders List ▌ -->
      <div v-show="currentTab === 'orders'">
        <div class="mfg-header">
          <h4 class="mfg-title">Orders</h4>
          <button class="btn btn-sm btn-primary" @click="openModal('newOrder')">
            <i class="bi bi-plus-lg mr-1"></i>New
          </button>
        </div>

        <!-- Filters -->
        <div class="filter-bar">
          <input type="text" class="form-control" v-model="orderFilters.search"
            @input="debounceLoadOrders" placeholder="Search..." style="flex:1" />
          <select class="form-control" v-model="orderFilters.status" @change="loadOrders" style="width:140px">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending</option>
            <option value="approved">Approved</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div v-if="ordersLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary"></div>
        </div>
        <div v-else>
          <div class="mfg-box" v-if="orders.length">
          <div class="order-card" v-for="order in orders" :key="order.id" @click="openOrderDetail(order.id)">
            <div class="order-card-top">
              <div>
                <span class="order-number">{{ order.order_number }}</span>
                <span :class="statusClass(order.status)" class="status-pill">{{ orderStatusLabel(order) }}</span>
              </div>
              <span class="order-time">{{ formatDate(order.created_at) }}</span>
            </div>
            <div class="order-product">{{ order.output_product && order.output_product.product }}</div>
            <div class="order-card-bottom">
              <span class="order-meta"><i class="bi bi-box"></i> {{ order.planned_batch_size }}</span>
              <span v-if="order.efficiency > 0" class="order-meta" :class="order.efficiency >= 90 ? 'text-dark' : 'text-danger'">
                {{ order.efficiency }}% efficiency
              </span>
              <i class="bi bi-chevron-right text-muted"></i>
            </div>
          </div>
          </div>
          <div v-if="!orders.length" class="empty-state">
            <p class="text-muted">No orders found</p>
          </div>
          <!-- Pagination -->
          <div v-if="orderPagination.total > orderPagination.perPage" class="d-flex justify-content-center mt-3">
            <b-pagination v-model="orderPagination.currentPage" :total-rows="orderPagination.total" :per-page="orderPagination.perPage" size="sm" @change="loadOrders" />
          </div>
        </div>
      </div>

      <!-- ▌TAB: BOMs ▌ -->
      <div v-show="currentTab === 'boms'">
        <div class="mfg-header">
          <h4 class="mfg-title">Bill of Materials</h4>
          <button v-if="canEditRecipes" class="btn btn-sm btn-primary" @click="openModal('newBom')">
            <i class="bi bi-plus-lg mr-1"></i>New BOM
          </button>
        </div>
        <div class="filter-bar">
          <input type="text" class="form-control" v-model="bomSearch" @input="debounceBoms" placeholder="Search BOMs..." style="flex:1" />
        </div>
        <div class="mfg-box" v-if="boms.length">
        <div class="order-card" v-for="bom in boms" :key="bom.id" @click="openModal('viewBom', bom)">
          <div class="order-card-top">
            <div>
              <span class="order-number"><i v-if="bom.grouped_inventory && bom.grouped_inventory.is_protected" class="bi bi-lock-fill mr-1" title="Protected with a PIN"></i>{{ bom.name }}</span>
              <span class="status-pill" :class="bom.status === 'approved' ? 'status-completed' : bom.status === 'pending_approval' ? 'status-pending' : 'status-draft'">
                {{ formatStatus(bom.status || (bom.is_active ? 'approved' : 'draft')) }}
              </span>
            </div>
            <span class="order-time">v{{ bom.version }}</span>
          </div>
          <div class="order-product">{{ bom.grouped_inventory && bom.grouped_inventory.group_title }}</div>
          <div class="order-card-bottom">
            <span class="order-meta"><i class="bi bi-layers"></i> {{ bom.stages && bom.stages.length }} stages</span>
            <span class="order-meta"><i class="bi bi-box"></i> {{ bom.batch_size }} {{ bom.batch_unit }}</span>
            <i class="bi bi-chevron-right text-muted"></i>
          </div>
        </div>
        </div>
        <div v-if="!boms.length && !bomsLoading" class="empty-state">
          <p class="text-muted">No BOMs yet</p>
          <button v-if="canEditRecipes" class="btn btn-sm btn-outline-primary mt-2" @click="openModal('newBom')">Create first BOM</button>
        </div>
      </div>

      <!-- ▌TAB: More ▌ -->
      <div v-show="currentTab === 'more'">
        <div class="mfg-header">
          <h4 class="mfg-title">Tools</h4>
        </div>
        <div class="more-grid">

          <div class="more-card" @click="openModal('newOrder')">
            <i class="bi bi-plus-circle-fill more-card-icon text-primary"></i>
            <span class="more-card-label">New Order</span>
            <small class="more-card-sub">Start a production run</small>
          </div>

          <div v-if="canEditRecipes" class="more-card" @click="openModal('newBom')">
            <i class="bi bi-file-earmark-plus-fill more-card-icon text-dark"></i>
            <span class="more-card-label">New BOM</span>
            <small class="more-card-sub">Create or edit a recipe</small>
          </div>

          <div class="more-card" @click="openModal('analytics')">
            <i class="bi bi-bar-chart-line-fill more-card-icon text-dark"></i>
            <span class="more-card-label">Analytics</span>
            <small class="more-card-sub">Batch costs &amp; efficiency</small>
          </div>

          <div class="more-card" @click="openModal('stockLedger')">
            <i class="bi bi-journal-text more-card-icon text-dark"></i>
            <span class="more-card-label">Stock Ledger</span>
            <small class="more-card-sub">Full movement history</small>
          </div>

          <div class="more-card" @click="openPackingHistory">
            <i class="bi bi-clock-history more-card-icon text-dark"></i>
            <span class="more-card-label">Packing history</span>
            <small class="more-card-sub">Every packing session</small>
          </div>

          <div class="more-card" @click="$refs.repackModal.open()">
            <i class="bi bi-bag-fill more-card-icon text-dark"></i>
            <span class="more-card-label">Pack produced stock</span>
            <small class="more-card-sub">Fill bags/bottles from what you produced</small>
          </div>

          <div class="more-card" @click="openModal('wastageLog')">
            <i class="bi bi-trash-fill more-card-icon text-danger"></i>
            <span class="more-card-label">Wastage Log</span>
            <small class="more-card-sub">All recorded wastage</small>
            <span v-if="kpis.abnormal_wastage_count > 0" class="more-card-badge badge badge-danger">{{ kpis.abnormal_wastage_count }}</span>
          </div>

          <div class="more-card" @click="openModal('lowStock')">
            <i class="bi bi-exclamation-triangle-fill more-card-icon text-danger"></i>
            <span class="more-card-label">Low Stock</span>
            <small class="more-card-sub">Materials below minimum</small>
            <span v-if="kpis.low_stock_count > 0" class="more-card-badge badge badge-secondary">{{ kpis.low_stock_count }}</span>
          </div>

        </div>
      </div>

    </div>

    <div class="mfg-content pt-0">
      <!-- Same Grouped Inventory screen as /group-inventory, shown here without leaving Manufacturing -->
      <div v-if="visited.grouped" v-show="currentTab === 'grouped'"><grouped-inventory embedded></grouped-inventory></div>
      <div v-if="visited.lots" v-show="currentTab === 'lots'"><lots-panel @trace="traceLot"></lots-panel></div>
      <div v-if="visited.trace" v-show="currentTab === 'trace'"><traceability-panel ref="tracePanel"></traceability-panel></div>
      <div v-if="visited.delivery" v-show="currentTab === 'delivery'"><delivery-notes-panel></delivery-notes-panel></div>
      <div v-if="visited.reports" v-show="currentTab === 'reports'"><manufacturing-reports></manufacturing-reports></div>
      <div v-if="visited.settings" v-show="currentTab === 'settings'"><inventory-settings-panel></inventory-settings-panel></div>
    </div>

    <!-- ════════════════════════════════════════════════════════════════
         ORDER DETAIL SLIDE-OVER PANEL
    ═══════════════════════════════════════════════════════════════════ -->
    <transition name="slide-over">
      <div v-if="activeOrderId" class="slide-over-panel">
        <div class="slide-over-header">
          <button class="btn-back" @click="closeOrderDetail">
            <i class="bi bi-arrow-left"></i>
          </button>
          <div class="flex-grow-1">
            <h5 class="mb-0" v-if="activeOrder">{{ activeOrder.order_number }}</h5>
            <div class="spinner-border spinner-border-sm text-primary" v-else></div>
          </div>
          <button class="btn-icon" @click="refreshActiveOrder"><i class="bi bi-arrow-clockwise"></i></button>
        </div>

        <div v-if="activeOrder" class="slide-over-body">
          <!-- Status + primary action -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span :class="statusClass(activeOrder.status)" class="status-pill status-pill--lg">{{ orderStatusLabel(activeOrder) }}</span>
            <div style="display:flex; gap:6px;">
              <button v-if="activeOrder.status === 'draft'" class="btn btn-sm btn-primary" @click="submitForApproval">Submit for approval</button>
              <button v-if="activeOrder.status === 'pending_approval'" class="btn btn-sm btn-primary" @click="openModal('approve')">Approve</button>
              <button v-if="activeOrder.status === 'pending_approval'" class="btn btn-sm btn-outline-secondary" @click="openReason('returnToDraft')">Return to draft</button>
              <button v-if="activeOrder.status === 'approved'" class="btn btn-sm btn-primary" @click="startProduction">Start</button>
              <button v-if="activeOrder.status === 'in_progress' && allStagesComplete" class="btn btn-sm btn-primary" @click="openModal('complete')">Complete</button>
              <button v-if="activeOrder.status === 'completed' && activeOrder.quality_status === 'pending'" class="btn btn-sm btn-primary" @click="openReason('release')">Quality release</button>
              <a :href="`/manufacturing/orders/${activeOrder.id}/print`" target="_blank" class="btn btn-sm btn-outline-dark" title="Production sheet"><i class="bi bi-printer"></i></a>
              <a v-if="activeOrder.status === 'completed'" :href="`/manufacturing/orders/${activeOrder.id}/label`" target="_blank" class="btn btn-sm btn-outline-dark" title="Batch label"><i class="bi bi-tag"></i></a>
              <button v-if="activeOrder.status === 'completed'" class="btn btn-sm btn-outline-danger" @click="openReason('reverse')">Reverse</button>
              <button v-if="!['completed','cancelled','reversed'].includes(activeOrder.status)" class="btn btn-sm btn-outline-danger" @click="openModal('cancel')">Cancel</button>
            </div>
          </div>

          <!-- Info pills -->
          <div class="info-pills mb-3">
            <div class="info-pill">
              <span class="info-pill-label">Product</span>
              <span class="info-pill-value">{{ activeOrder.output_product && activeOrder.output_product.product }}</span>
            </div>
            <div class="info-pill">
              <span class="info-pill-label">Planned</span>
              <span class="info-pill-value">{{ activeOrder.planned_batch_size }} {{ activeOrder.bom && activeOrder.bom.batch_unit }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.actual_batch_size">
              <span class="info-pill-label">Actual</span>
              <span class="info-pill-value" :class="activeOrder.efficiency >= 90 ? 'text-dark' : 'text-danger'">{{ activeOrder.actual_batch_size }}</span>
            </div>
            <div class="info-pill">
              <span class="info-pill-label">Planned Cost</span>
              <span class="info-pill-value">{{ formatCurrency(activeOrder.planned_cost) }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.actual_cost">
              <span class="info-pill-label">Actual Cost</span>
              <span class="info-pill-value" :class="activeOrder.cost_variance > 0 ? 'text-danger' : 'text-dark'">{{ formatCurrency(activeOrder.actual_cost) }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.output_unit_cost">
              <span class="info-pill-label">Unit cost</span>
              <span class="info-pill-value">{{ formatCurrency(activeOrder.output_unit_cost) }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.material_cost != null">
              <span class="info-pill-label">Materials / conversion / abnormal loss</span>
              <span class="info-pill-value">{{ formatCurrency(activeOrder.material_cost) }} / {{ formatCurrency(activeOrder.conversion_cost) }} / {{ formatCurrency(activeOrder.abnormal_loss_cost) }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.output_lot">
              <span class="info-pill-label">Batch</span>
              <span class="info-pill-value">{{ activeOrder.output_lot.lot_number }} ({{ activeOrder.output_lot.status }})</span>
            </div>
            <div class="info-pill" v-if="activeOrder.quality_status && activeOrder.quality_status !== 'not_required'">
              <span class="info-pill-label">Quality</span>
              <span class="info-pill-value" :class="activeOrder.quality_status === 'rejected' ? 'text-danger' : ''">{{ formatStatus(activeOrder.quality_status) }}</span>
            </div>
            <div class="info-pill" v-if="activeOrder.efficiency > 0">
              <span class="info-pill-label">Efficiency</span>
              <span class="info-pill-value" :class="activeOrder.efficiency >= 90 ? 'text-dark' : 'text-danger'">{{ activeOrder.efficiency }}%</span>
            </div>
          </div>

          <!-- Stages pipeline -->
          <div v-if="['in_progress','paused','completed'].includes(activeOrder.status)" class="mb-3">
            <h6 class="section-title mb-2">Stages</h6>
            <div class="stage-row" v-for="(stage, si) in activeOrder.stages" :key="stage.id">
              <div class="stage-dot" :class="'stage-dot--' + stage.status"></div>
              <div class="stage-body">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="stage-name">{{ stage.bom_stage && stage.bom_stage.name }}</span>
                  <div style="display:flex; gap:4px;">
                    <button
                      v-if="stage.status === 'pending' && si === nextPendingIndex"
                      class="btn btn-xs btn-primary"
                      @click="startStage(stage)"
                    >Start</button>
                    <button
                      v-if="stage.status === 'in_progress'"
                      class="btn btn-xs btn-primary"
                      @click="openModal('completeStage', stage)"
                    >Done</button>
                    <button
                      v-if="['pending','in_progress'].includes(stage.status)"
                      class="btn btn-xs btn-outline-secondary"
                      title="Skip a stage that did not happen for this batch"
                      @click="openReason('skipStage', stage)"
                    >Skip</button>
                    <button
                      v-if="['in_progress','completed'].includes(stage.status)"
                      class="btn btn-xs btn-outline-secondary"
                      @click="openModal('wastage', stage)"
                    ><i class="bi bi-trash"></i></button>
                  </div>
                </div>
                <div v-if="stage.status === 'skipped'" class="stage-meta">{{ stage.notes }}</div>
                <div v-if="stage.status === 'completed'" class="stage-meta">
                  Out: {{ stage.output_quantity || '—' }} &nbsp;·&nbsp; Wastage: {{ stage.actual_wastage || 0 }}
                  &nbsp;·&nbsp; {{ stageDuration(stage) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Availability (before stock is consumed) -->
          <div v-if="activeOrder.availability && activeOrder.availability.length" class="mb-3">
            <h6 class="section-title mb-2">Material availability</h6>
            <div v-if="activeOrder.availability.some(a => a.is_short)" class="alert alert-danger py-2 small mb-2">Some materials are short. Approval will be refused until stock is received or released.</div>
            <table class="table table-sm mb-0">
              <thead><tr><th>Material</th><th class="text-right">Required</th><th class="text-right">Free</th><th class="text-right">Reserved by other orders</th></tr></thead>
              <tbody>
                <tr v-for="a in activeOrder.availability" :key="a.product_id">
                  <td>{{ a.product }} <span v-if="a.track_lots" class="badge badge-secondary">lots</span><span v-if="a.unit_cost == null" class="badge badge-danger ml-1">no cost</span></td>
                  <td class="text-right">{{ a.required }} {{ a.unit }}</td>
                  <td class="text-right" :class="a.is_short ? 'text-danger font-weight-bold' : ''">{{ a.available }}</td>
                  <td class="text-right text-muted">{{ a.reserved }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Materials -->
          <div class="mb-3">
            <h6 class="section-title mb-2">Materials</h6>
            <div class="material-row" v-for="u in activeOrder.material_usages" :key="u.id">
              <div class="material-name">
                {{ u.product && u.product.product }}
                <span v-if="u.is_substitute" class="badge badge-secondary badge-sm ml-1">Sub</span>
              </div>
              <div class="material-qty">
                <span class="text-muted">{{ u.planned_quantity }} {{ u.unit }}</span>
                <span v-if="u.actual_quantity != null"> → <strong>{{ u.actual_quantity }}</strong></span>
              </div>
              <div class="material-cost text-muted">{{ formatCurrency(u.actual_unit_cost != null ? u.actual_unit_cost : u.unit_cost_at_time) }}</div>
            </div>
          </div>

          <!-- Record wastage (always available when in progress) -->
          <button v-if="activeOrder.status === 'in_progress'" class="btn btn-outline-secondary btn-block btn-sm mb-3" @click="openModal('wastage', null)">
            <i class="bi bi-trash mr-1"></i> Record Wastage
          </button>

          <!-- Wastage records -->
          <div v-if="activeOrder.wastages && activeOrder.wastages.length" class="mb-3">
            <h6 class="section-title mb-2">Wastage</h6>
            <div class="wastage-row" v-for="w in activeOrder.wastages" :key="w.id">
              <i class="bi bi-circle-fill" :class="w.wastage_type === 'abnormal' ? 'text-danger' : 'text-danger'" style="font-size:8px;"></i>
              <span>{{ w.quantity }} {{ w.unit }} {{ w.product && w.product.product }}</span>
              <span class="text-muted ml-auto">{{ w.reason }}</span>
              <span v-if="w.wastage_type === 'abnormal'" class="badge badge-danger badge-sm ml-1">!</span>
            </div>
          </div>

          <!-- Notes -->
          <div v-if="activeOrder.notes" class="order-notes">{{ activeOrder.notes }}</div>

          <!-- Packing stage: after mixing, the loose batch is packed (skippable) -->
          <div v-if="activeOrder.packing_status" class="mb-3">
            <h6 class="section-title mb-2">Packing</h6>
            <div v-if="['awaiting','in_progress'].includes(activeOrder.packing_status)" class="alert alert-secondary py-2 d-flex flex-wrap align-items-center" style="gap:8px;">
              <span>
                <strong>{{ activeOrder.packing_status === 'awaiting' ? 'Mixed — awaiting packing.' : 'Packing in progress.' }}</strong>
                The batch is in stock as {{ activeOrder.output_product && activeOrder.output_product.product }}.
              </span>
              <button v-if="plannedPackLines.length && activeOrder.packing_status === 'awaiting'" class="btn btn-primary btn-sm ml-auto" :disabled="packingPlanned" @click="packAsPlanned">
                <span v-if="packingPlanned" class="spinner-border spinner-border-sm mr-1"></span><i v-else class="bi bi-box-seam mr-1"></i>Pack as planned
              </button>
              <button class="btn btn-sm" :class="plannedPackLines.length && activeOrder.packing_status === 'awaiting' ? 'btn-outline-primary' : 'btn-primary ml-auto'" @click="$refs.repackModal.open(null, activeOrder.id)"><i class="bi bi-box-seam mr-1"></i>{{ plannedPackLines.length && activeOrder.packing_status === 'awaiting' ? 'Pack differently' : 'Pack' }}</button>
            </div>
            <div v-else class="small text-muted mb-1">{{ activeOrder.packing_status === 'skipped' ? 'Packing skipped — the batch stayed loose.' : 'Packing finished.' }}</div>
            <div v-if="plannedPackLines.length && activeOrder.packing_status === 'awaiting'" class="small mb-1">
              <span class="text-muted">Planned:</span>
              <span v-for="(l, i) in plannedPackLines" :key="l.id">{{ i ? ', ' : '' }}{{ l.quantity }} × {{ l.name }}</span>
            </div>
            <div class="small" v-for="ps in (activeOrder.packing_sessions || [])" :key="ps.id">
              <strong>{{ ps.number }}</strong>
              <span :class="ps.reversed_at ? 'text-muted' : ''" :style="ps.reversed_at ? 'text-decoration: line-through;' : ''">
                <span v-for="(l, i) in ps.lines" :key="l.id">{{ i ? ', ' : '' }}{{ Number(l.packs) }} × {{ l.product && l.product.product }}</span>
              </span>
              <span class="text-muted"> · {{ ps.created_by && ps.created_by.name }}<span v-if="ps.reversed_at"> · reversed</span></span>
            </div>
          </div>
          <div v-else-if="plannedPackLines.length && !(activeOrder.outputs && activeOrder.outputs.length)" class="mb-3">
            <h6 class="section-title mb-2">Bags to fill</h6>
            <div class="small" v-for="l in plannedPackLines" :key="l.id">
              <strong>{{ l.quantity }} × {{ l.name }}</strong>
              <span class="text-muted">{{ l.content }} each</span>
            </div>
          </div>

          <!-- Older orders packed in the completion step -->
          <div v-if="activeOrder.outputs && activeOrder.outputs.length" class="mb-3">
            <h6 class="section-title mb-2">Packed</h6>
            <div class="small" v-for="o in activeOrder.outputs" :key="o.id">
              <strong>{{ Number(o.packs) }} × {{ o.product && o.product.product }}</strong>
              <span class="text-muted">at {{ formatCurrency(o.unit_cost) }} each<span v-if="o.lot"> · batch {{ o.lot.lot_number }}</span></span>
            </div>
          </div>

          <!-- Approval & audit history -->
          <div v-if="activeOrder.events && activeOrder.events.length" class="mb-3">
            <h6 class="section-title mb-2">History</h6>
            <div class="small" v-for="ev in activeOrder.events" :key="ev.id">
              <span class="text-muted">{{ (ev.created_at || '').substring(0, 16).replace('T', ' ') }}</span>
              · <strong>{{ formatStatus(ev.action) }}</strong> by {{ ev.user ? ev.user.name : 'system' }}
              <span v-if="ev.notes" class="text-muted">— {{ ev.notes }}</span>
            </div>
          </div>

          <!-- Footer: created / approved -->
          <div class="order-footer-meta">
            <span>Created by {{ activeOrder.created_by && activeOrder.created_by.name }}</span>
            <span v-if="activeOrder.approved_by">· Approved by {{ activeOrder.approved_by.name }}</span>
          </div>
        </div>

        <div v-else class="slide-over-body text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>
      </div>
    </transition>

    <!-- Backdrop for slide-over -->
    <transition name="fade">
      <div v-if="activeOrderId" class="slide-over-backdrop" @click="closeOrderDetail"></div>
    </transition>

    <!-- ════════════════════════════════════════════════════════════════
         MODALS
    ═══════════════════════════════════════════════════════════════════ -->

    <!-- New Order Modal -->
    <b-modal ref="newOrderModal" title="New Production Order" hide-footer size="lg" @hidden="resetNewOrder">

      <!-- ── Step 1: Product search ──────────────────────────────────── -->
      <div class="form-group">
        <label class="form-label-sm">What are you producing? <span class="text-danger">*</span></label>
        <div class="position-relative">
          <input
            type="text"
            class="form-control"
            v-model="newOrder.productSearch"
            @input="searchManufactured"
            placeholder="Recipe, finished product or grouped item, e.g. Dairy Meal, Chapati..."
            autocomplete="off"
            :disabled="newOrder.loadingBoms"
          />
          <div v-if="newOrder.productSuggestions.length || newOrder.bundleSuggestions.length" class="typeahead-drop">
            <div v-if="newOrder.productSuggestions.length" class="typeahead-group">Recipes &amp; products</div>
            <button
              v-for="p in newOrder.productSuggestions"
              :key="p.id"
              type="button"
              class="typeahead-item"
              @mousedown.prevent="selectManufacturedProduct(p)"
            >
              <div>
                <strong><i v-if="p.is_protected" class="bi bi-lock-fill mr-1" title="Protected with a PIN"></i>{{ p.recipe ? p.recipe.name : p.group_title }}</strong>
                <span v-if="p.recipe" class="badge badge-secondary ml-1">v{{ p.recipe.version }} · {{ formatStatus(p.recipe.status) }}</span>
                <small class="text-muted d-block">
                  <span v-if="p.output_product">Makes {{ p.output_product.product }} · {{ p.output_product.remaining_units }} in stock</span>
                  <span v-else>{{ p.group_title }}</span>
                  <span v-if="p.versions_count > 1"> · {{ p.versions_count }} versions</span>
                </small>
              </div>
              <small class="text-muted">{{ p.recipe ? 'recipe' : p.items_count + ' item(s), no recipe yet' }}</small>
            </button>
            <div v-if="newOrder.bundleSuggestions.length" class="typeahead-group">Grouped items (sold as bundles at the counter)</div>
            <button
              v-for="b in newOrder.bundleSuggestions"
              :key="b.id"
              type="button"
              class="typeahead-item"
              @mousedown.prevent="selectBundle(b)"
            >
              <div><strong>{{ b.group_title }}</strong><small class="text-muted d-block">Use its items as a recipe</small></div>
              <small class="text-muted">{{ b.items_count }} item(s)</small>
            </button>
          </div>
        </div>
      </div>

      <!-- ── Bundle chosen: its items become a recipe for a finished product ── -->
      <div v-if="newOrder.bundle" class="no-bom-prompt">
        <div class="no-bom-icon"><i class="bi bi-boxes"></i></div>
        <div class="no-bom-body" style="flex:1;">
          <p class="font-weight-bold mb-1">Produce from "{{ newOrder.bundle.group_title }}"</p>
          <p class="text-muted small mb-2">
            This is a bundle sold at the counter. Choose the finished product it makes; its {{ newOrder.bundle.items_count }} item(s) are copied into a draft recipe for that product.
            The bundle itself is not changed.
          </p>
          <div class="no-bom-ingredients mb-2">
            <div class="ingredient-pill" v-for="ing in newOrder.bundle.grouped_items.slice(0, 4)" :key="ing.id">
              <span>{{ ing.product ? ing.product.product : 'Unknown' }}</span>
              <span class="text-muted ml-1">× {{ ing.count }} {{ ing.product ? (ing.product.measuring_unit || 'pcs') : '' }}</span>
            </div>
            <span v-if="newOrder.bundle.items_count > 4" class="ingredient-pill ingredient-pill--more">+{{ newOrder.bundle.items_count - 4 }} more</span>
          </div>
          <div class="position-relative mb-2">
            <input type="text" class="form-control" v-model="newOrder.bundleProductSearch" @input="searchBundleOutput"
              placeholder="Finished product, e.g. Dairy Meal..." autocomplete="off" />
            <div v-if="newOrder.bundleProductSuggestions.length" class="typeahead-drop">
              <button v-for="p in newOrder.bundleProductSuggestions" :key="p.id" type="button" class="typeahead-item"
                @mousedown.prevent="pickBundleOutput(p)">
                <div><strong>{{ p.product }}</strong><small class="text-muted d-block">{{ p.remaining_units }} {{ p.unit || '' }} in stock<span v-if="p.has_approved_recipe"> · already has a recipe (this adds a new version)</span></small></div>
              </button>
            </div>
          </div>
          <div class="d-flex" style="gap:8px; flex-wrap:wrap;">
            <button class="btn btn-primary btn-sm" :disabled="!newOrder.bundleOutput || newOrder.generatingBom" @click="recipeFromBundle">
              <span v-if="newOrder.generatingBom" class="spinner-border spinner-border-sm mr-1"></span>Create recipe from these items
            </button>
            <small class="text-muted align-self-center">No finished product yet? Add it in Inventory first.</small>
          </div>
        </div>
      </div>

      <!-- ── Loading spinner while fetching BOM ─────────────────────── -->
      <div v-if="newOrder.loadingBoms" class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary mr-2"></div>
        <small class="text-muted">Checking recipe...</small>
      </div>

      <!-- ── NO BOM + HAS INGREDIENTS: offer auto-generate ──────────── -->
      <div v-else-if="newOrder.showNoBomPrompt" class="no-bom-prompt">
        <div class="no-bom-icon">
          <i class="bi bi-journal-plus"></i>
        </div>
        <div class="no-bom-body">
          <p class="font-weight-bold mb-1">No recipe found for "{{ newOrder.productSearch }}"</p>
          <p class="text-muted small mb-3">
            We found <strong>{{ newOrder.ingredientCount }} ingredient(s)</strong> already configured for this product.
            We can create a recipe from them automatically so you can start right away.
          </p>
          <div class="no-bom-ingredients mb-3" v-if="newOrder.ingredientPreview.length">
            <div
              class="ingredient-pill"
              v-for="ing in newOrder.ingredientPreview"
              :key="ing.product_id"
            >
              <span>{{ ing.product }}</span>
              <span class="text-muted ml-1">× {{ ing.count }} {{ ing.unit }}</span>
            </div>
            <span v-if="newOrder.ingredientCount > 4" class="ingredient-pill ingredient-pill--more">
              +{{ newOrder.ingredientCount - 4 }} more
            </span>
          </div>
          <p v-if="!canEditRecipes" class="small text-muted mb-0">Ask someone with recipe access to set up its recipe.</p>
          <div v-if="canEditRecipes" class="d-flex" style="gap:8px; flex-wrap:wrap;">
            <button
              class="btn btn-primary btn-sm"
              @click="autoGenerateBom"
              :disabled="newOrder.generatingBom"
            >
              <span v-if="newOrder.generatingBom" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-magic mr-1"></i>
              {{ newOrder.generatingBom ? 'Creating recipe...' : 'Use its grouped items' }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="goToRecipes">
              <i class="bi bi-pencil mr-1"></i> Set up recipe manually
            </button>
          </div>
          <small class="text-muted d-block mt-2">
            You can always refine the recipe later under Recipes.
          </small>
        </div>
      </div>

      <!-- ── NO BOM + NO INGREDIENTS: guide to set up ───────────────── -->
      <div v-else-if="newOrder.showEmptyPrompt" class="no-bom-prompt no-bom-prompt--empty">
        <div class="no-bom-icon">
          <i class="bi bi-exclamation-circle"></i>
        </div>
        <div class="no-bom-body">
          <p class="font-weight-bold mb-1">No recipe or materials found</p>
          <p class="text-muted small mb-3">
            "{{ newOrder.productSearch }}" has no materials listed yet.
            Add its materials under Grouped items first, then come back to create an order.
          </p>
          <p v-if="!canEditRecipes" class="small text-muted mb-0">Ask someone with recipe access to add them.</p>
          <div v-if="canEditRecipes" class="d-flex" style="gap:8px;">
            <button class="btn btn-primary btn-sm" @click="goToGroupedInventory">
              <i class="bi bi-list-check mr-1"></i> Add materials
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="goToRecipes">
              <i class="bi bi-journal-plus mr-1"></i> Create recipe manually
            </button>
          </div>
        </div>
      </div>

      <!-- ── BOM auto-generated success flash ───────────────────────── -->
      <div v-if="newOrder.justGenerated" class="alert alert-secondary py-2 d-flex align-items-center mb-3">
        <i class="bi bi-check-circle-fill mr-2"></i>
        <span>Draft recipe created from its grouped items. Check it, then submit it for approval below — production can start once a second person approves it.</span>
      </div>

      <!-- ── BOM resolved: quiet confirmation + version picker ──────── -->
      <div v-if="newOrder.bom_id && newOrder.selectedBom && !newOrder.showNoBomPrompt && !newOrder.showEmptyPrompt">

        <!-- Confirmed BOM pill -->
        <div class="bom-confirmed-pill mb-3">
          <i class="bi bi-check-circle-fill text-dark mr-2"></i>
          <span class="font-weight-bold">{{ newOrder.selectedBom.name }}</span>
          <span class="text-muted ml-2">
            v{{ newOrder.selectedBom.version }}
            · {{ newOrder.selectedBom.stages && newOrder.selectedBom.stages.length }} stage(s)
            · Base batch: {{ newOrder.selectedBom.batch_size }} {{ newOrder.selectedBom.batch_unit }}
          </span>
          <!-- Version switcher only shown when >1 version exists -->
          <button
            v-if="newOrder.bomVersions.length > 1"
            class="btn btn-link btn-sm ml-auto p-0"
            @click="newOrder.showVersionPicker = !newOrder.showVersionPicker"
          >{{ newOrder.showVersionPicker ? 'Close' : 'Switch version' }}</button>
        </div>

        <!-- A recipe must be approved before it can be used for production -->
        <div v-if="!selectedBomUsable" class="alert alert-secondary py-2 mb-3">
          <div>Recipe v{{ newOrder.selectedBom.version }} is <strong>{{ formatStatus(newOrder.selectedBom.status || 'draft') }}</strong>. It has to be approved by a second person before production can use it.</div>
          <div class="mt-2" style="display:flex; gap:8px;">
            <button v-if="canEditRecipes && ['draft','rejected'].includes(newOrder.selectedBom.status)" class="btn btn-sm btn-primary" :disabled="newOrder.submittingBom" @click="submitSelectedBom">Submit for approval</button>
            <button v-if="canViewRecipes" class="btn btn-sm btn-outline-secondary" @click="goToRecipes">Open Recipes</button>
          </div>
        </div>

        <div v-if="newOrder.showVersionPicker" class="form-group mb-3">
          <select class="form-control" v-model="newOrder.bom_id" @change="onBomSelected">
            <option v-for="bom in newOrder.bomVersions" :key="bom.id" :value="bom.id">
              v{{ bom.version }} — {{ bom.name }} ({{ formatStatus(bom.status || (bom.is_active ? 'approved' : 'draft')) }})
            </option>
          </select>
        </div>

        <!-- ── Step 2: Batch details ─────────────────────────────────── -->
        <div class="form-row">
          <div class="form-group col-md-5">
            <label class="form-label-sm">Batch Size <span class="text-danger">*</span></label>
            <div class="input-group">
              <input
                type="number"
                class="form-control"
                v-model.number="newOrder.planned_batch_size"
                @input="debounceAvailability"
                min="0.0001"
                step="any"
                :placeholder="newOrder.selectedBom && newOrder.selectedBom.batch_size"
                autofocus
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ newOrder.selectedBom && newOrder.selectedBom.batch_unit }}</span>
              </div>
            </div>
            <small class="text-muted">How many units to produce in this run</small>
          </div>
          <div class="form-group col-md-7">
            <label class="form-label-sm">Start date &amp; time</label>
            <date-time-field v-model="newOrder.planned_start_at"></date-time-field>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group col-12">
            <label class="form-label-sm">Notes</label>
            <input type="text" class="form-control" v-model="newOrder.notes" placeholder="Optional..." />
          </div>
        </div>

        <!-- ── Bag plan: how the batch will be packed (optional) ─────── -->
        <div v-if="newOrder.packSizes.length" class="mb-3">
          <label class="form-label-sm mb-1">Bags to fill <small class="text-muted">optional — shown on the batch sheet and pre-filled at completion</small></label>
          <div class="material-table">
            <div class="material-row" v-for="p in newOrder.packSizes" :key="p.id">
              <span class="material-name">{{ p.product && p.product.product }}</span>
              <span class="material-qty text-muted">{{ Number(p.content_quantity) }} {{ p.content_unit }} each</span>
              <div class="input-group" style="max-width:150px;">
                <input type="number" min="0" step="1" class="form-control" v-model.number="p.planned" placeholder="0" />
                <div class="input-group-append"><span class="input-group-text">bags</span></div>
              </div>
              <small v-if="p.packaging_product && Number(p.planned) > 0" class="w-100 pl-3" :class="bagShort(p) ? 'text-danger' : 'text-muted'">
                Uses {{ Number(p.planned) * Number(p.packaging_quantity || 1) }} × {{ p.packaging_product.product }} ({{ Number(p.packaging_product.remaining_units || 0) }} in stock)
              </small>
            </div>
          </div>
          <small v-if="bagPlanSummary" :class="['d-block mt-1', bagPlanSummary.over ? 'text-danger' : 'text-muted']">{{ bagPlanSummary.text }}</small>
        </div>

        <!-- ── Step 3: Availability widget ──────────────────────────── -->
        <div v-if="newOrder.planned_batch_size" class="availability-widget">
          <div class="d-flex align-items-center mb-2">
            <span class="font-weight-bold mr-2" style="font-size:13px;">Material check</span>
            <div v-if="newOrder.checking" class="spinner-border spinner-border-sm text-primary mr-2"></div>
            <template v-else-if="newOrder.availability">
              <span v-if="newOrder.availability.available" class="badge badge-primary">
                <i class="bi bi-check-lg mr-1"></i>All available
              </span>
              <span v-else class="badge badge-danger">
                {{ newOrder.availability.materials.filter(m => m.is_short).length }} shortage(s)
              </span>
            </template>
            <span class="ml-auto text-muted" v-if="newOrder.availability">
              Estimated cost: <strong>{{ newOrder.availability.estimated_cost == null ? 'missing material costs' : formatCurrency(newOrder.availability.estimated_cost) }}</strong>
            </span>
          </div>
          <div v-if="newOrder.availability" class="material-table">
            <div
              class="material-row"
              v-for="m in newOrder.availability.materials"
              :key="m.product_id"
              :class="{
                'material-row--short': m.is_short && !m.has_substitute,
                'material-row--warn':  m.is_short &&  m.has_substitute
              }"
            >
              <span class="material-name">{{ m.product }}</span>
              <span class="material-qty text-muted">{{ m.required }} {{ m.unit }} needed</span>
              <span class="material-qty" :class="m.available < m.required ? 'text-danger' : 'text-dark'">
                {{ m.available }} in stock
              </span>
              <span v-if="!m.is_short" class="badge badge-primary">OK</span>
              <span v-else-if="m.has_substitute" class="badge badge-secondary">
                <i class="bi bi-arrow-left-right mr-1"></i>Substitute
              </span>
              <span v-else-if="m.sub_recipe" class="badge badge-secondary">Make {{ m.sub_recipe.to_make }} {{ m.sub_recipe.unit }}</span>
              <span v-else class="badge badge-danger">Short {{ m.deficit }}</span>
              <div v-if="m.sub_recipe" class="w-100 small text-secondary pl-3 mt-1">
                Short {{ m.deficit }} {{ m.unit }} — make it first with <strong>{{ m.sub_recipe.name }}</strong> (v{{ m.sub_recipe.version }}):
                <span v-for="(sm, si) in m.sub_recipe.materials" :key="sm.product_id">{{ si ? ', ' : '' }}{{ sm.product }} {{ sm.required }} {{ sm.unit }}<span v-if="sm.is_short" class="text-danger"> (short {{ sm.deficit }})</span></span>
              </div>
            </div>
            <div v-if="newOrder.availability.shared_shortages && newOrder.availability.shared_shortages.length" class="small text-danger mt-1">
              Not enough for this order and its sub-recipes together:
              <span v-for="(x, xi) in newOrder.availability.shared_shortages" :key="x.product_id">{{ xi ? ', ' : '' }}{{ x.product }} (need {{ x.needed_from_stock }} {{ x.unit }}, free {{ x.free }})</span>
            </div>
          </div>
          <div
            v-if="newOrder.availability && !newOrder.availability.available"
            class="mt-2 small text-muted"
          >
            <i class="bi bi-info-circle mr-1"></i>
            You can still create a draft — shortages must be resolved before a manager can approve.
          </div>
        </div>

      </div>

      <!-- ── Footer ────────────────────────────────────────────────────── -->
      <div class="d-flex justify-content-end mt-3" style="gap:8px;">
        <button class="btn btn-outline-secondary" @click="$refs.newOrderModal.hide()">Cancel</button>
        <button
          class="btn btn-primary"
          @click="createOrder"
          :disabled="newOrder.saving || !newOrder.bom_id || !newOrder.planned_batch_size || !selectedBomUsable || (bagPlanSummary && bagPlanSummary.over)"
        >
          <span v-if="newOrder.saving" class="spinner-border spinner-border-sm mr-1"></span>
          Create Draft Order
        </button>
      </div>
    </b-modal>

    <!-- New BOM Modal -->
    <b-modal ref="newBomModal" :title="editingBom ? (editBomInPlace ? 'Edit v' + editingBom.version + ': ' : 'New version of: ') + editingBom.name : 'Create Bill of Materials'" hide-footer size="xl" @hidden="resetBomForm">
      <bom-form-inline ref="bomFormRef" :edit-bom="editingBom" :in-place="editBomInPlace" @saved="onBomSaved" @cancel="$refs.newBomModal.hide()" />
    </b-modal>

    <!-- View BOM Modal -->
    <b-modal ref="viewBomModal" :title="selectedBom ? selectedBom.name : 'BOM Details'" hide-footer size="lg">
      <div v-if="selectedBom">
        <div class="info-pills mb-3">
          <div class="info-pill"><span class="info-pill-label">Type</span><span class="info-pill-value">{{ selectedBom.bom_type }}</span></div>
          <div class="info-pill"><span class="info-pill-label">Batch</span><span class="info-pill-value">{{ selectedBom.batch_size }} {{ selectedBom.batch_unit }}</span></div>
          <div class="info-pill"><span class="info-pill-label">Yield</span><span class="info-pill-value">{{ selectedBom.expected_yield_pct }}%</span></div>
          <div class="info-pill"><span class="info-pill-label">Status</span><span class="info-pill-value">{{ formatStatus(selectedBom.status || (selectedBom.is_active ? 'approved' : 'draft')) }} · v{{ selectedBom.version }}</span></div>
          <div class="info-pill"><span class="info-pill-label">Conversion cost / {{ selectedBom.batch_unit }}</span><span class="info-pill-value">{{ formatCurrency(selectedBom.conversion_cost_per_unit || 0) }}</span></div>
          <div class="info-pill" v-if="selectedBom.approved_by"><span class="info-pill-label">Approved by</span><span class="info-pill-value">{{ selectedBom.approved_by.name || '—' }}</span></div>
          <div class="info-pill" v-if="selectedBom.self_approval_reason"><span class="info-pill-label">Self-approved because</span><span class="info-pill-value">{{ selectedBom.self_approval_reason }}</span></div>
        </div>
        <div v-for="stage in selectedBom.stages" :key="stage.id" class="bom-stage-preview mb-2">
          <div class="bom-stage-header">
            <span class="badge badge-primary mr-2">{{ stage.stage_order }}</span>
            <strong>{{ stage.name }}</strong>
            <small class="text-muted ml-2">{{ stage.expected_wastage_pct }}% expected loss</small>
          </div>
          <div class="material-row" v-for="m in stage.materials" :key="m.id">
            <span class="material-name">{{ m.product && m.product.product }}</span>
            <span class="material-qty">{{ m.quantity }} {{ m.unit }}</span>
            <span v-if="!m.is_required" class="badge badge-secondary badge-sm">Optional</span>
            <span v-if="m.is_substitutable" class="badge badge-secondary badge-sm">Substitutable</span>
            <span class="ml-auto text-right" style="min-width:90px;">
              <template v-if="bomLineValue(m) !== null">{{ formatCurrency(bomLineValue(m)) }}</template>
              <small v-else class="text-danger" :title="m.value_reason || ''">not counted</small>
            </span>
          </div>
        </div>
        <!-- What the recipe's materials cost at today's buying prices -->
        <div v-if="selectedBom.stages && selectedBom.stages.length" class="d-flex flex-wrap justify-content-end mb-3" style="gap:24px;">
          <div v-if="bomQtyText" class="text-right"><small class="text-muted d-block">Total quantity</small><strong class="text-dark">{{ bomQtyText }}</strong><small class="text-muted d-block">batch {{ selectedBom.batch_size }} {{ selectedBom.batch_unit }}</small></div>
          <div class="text-right"><small class="text-muted d-block">Materials value</small><strong class="text-dark">{{ formatCurrency(bomTotals.total) }}</strong></div>
          <div v-if="Number(selectedBom.batch_size) > 0" class="text-right"><small class="text-muted d-block">Per {{ selectedBom.batch_unit }} ({{ selectedBom.batch_size }} {{ selectedBom.batch_unit }} batch)</small><strong class="text-dark">{{ formatCurrency(bomTotals.total / Number(selectedBom.batch_size)) }}</strong></div>
          <div v-if="bomTotals.missing" class="text-danger small w-100 text-right">
            {{ bomTotals.missing }} item(s) not counted:
            <div v-for="(r, ri) in bomMissingReasons" :key="ri">{{ r }}</div>
          </div>
        </div>
        <!-- Pack sizes: one batch packed into several bag sizes at completion -->
        <div class="bom-stage-preview mb-2">
          <div class="bom-stage-header">
            <strong>Pack sizes</strong>
            <small class="text-muted ml-2">after mixing, the batch is packed into these (bags, bottles…); leave empty if it isn't packed</small>
          </div>
          <div class="material-row" v-for="ps in bomPackSizes" :key="ps.id">
            <span class="material-name">{{ ps.product && ps.product.product }}</span>
            <span class="material-qty">{{ ps.content_quantity }} {{ ps.content_unit }} per pack</span>
            <span v-if="ps.components && ps.components.length" class="text-muted small ml-2">uses <span v-for="(c, ci) in ps.components" :key="c.id">{{ ci ? ', ' : '' }}{{ Number(c.quantity) }} × {{ c.product && c.product.product }}</span></span>
            <span v-else-if="ps.packaging_product" class="text-muted small ml-2">uses {{ ps.packaging_quantity }} × {{ ps.packaging_product.product }}</span>
            <button class="btn btn-link btn-sm text-danger ml-auto p-0" title="Remove pack size" @click="removePackSize(ps)"><i class="bi bi-trash"></i></button>
          </div>
          <div v-if="!bomPackSizes.length" class="text-muted small py-1">No pack sizes yet.</div>
          <div class="form-row mt-2 align-items-end">
            <div class="form-group col-md-4 mb-2 position-relative">
              <label class="small mb-1">Packed product (counted per pack)</label>
              <input type="text" class="form-control" v-model="packForm.productSearch" @input="searchPackProduct('product')" placeholder="e.g. Dairy Meal 50kg" />
              <div v-if="packForm.productSuggestions.length" class="list-group w-100 mt-1" style="max-height:220px; overflow-y:auto;">
                <a v-for="p in packForm.productSuggestions" :key="p.id" href="#" class="list-group-item list-group-item-action py-1" @click.prevent="pickPackProduct('product', p)">{{ p.product }} <small class="text-muted">({{ p.measuring_unit }})</small></a>
              </div>
            </div>
            <div class="form-group col-md-2 mb-2">
              <label class="small mb-1">Mix per pack</label>
              <div class="input-group">
                <input type="number" class="form-control" v-model.number="packForm.content_quantity" min="0" step="any" />
                <div class="input-group-append"><span class="input-group-text">{{ selectedBom.batch_unit }}</span></div>
              </div>
            </div>
            <div class="form-group col-md-4 mb-2 position-relative">
              <label class="small mb-1">Packaging per pack (optional)</label>
              <input type="text" class="form-control" v-model="packForm.bagSearch" @input="searchPackProduct('bag')" placeholder="e.g. Empty 50kg bag, 1L bottle, cap" />
              <div v-if="packForm.bagSuggestions.length" class="list-group w-100 mt-1" style="max-height:220px; overflow-y:auto;">
                <a v-for="p in packForm.bagSuggestions" :key="p.id" href="#" class="list-group-item list-group-item-action py-1" @click.prevent="pickPackProduct('bag', p)">{{ p.product }}</a>
              </div>
              <div v-for="(k, ki) in packForm.kit" :key="k.product_id" class="d-flex align-items-center mt-1" style="gap:6px;">
                <input type="number" class="form-control" style="max-width:80px;" v-model.number="k.quantity" min="0.0001" step="any" />
                <span class="small">× {{ k.name }}</span>
                <button type="button" class="btn btn-link btn-sm text-danger p-0 ml-auto" @click="packForm.kit.splice(ki, 1)"><i class="bi bi-x"></i></button>
              </div>
            </div>
            <div class="form-group col-md-2 mb-2">
              <button class="btn btn-primary btn-block" :disabled="!packForm.product_id || !packForm.content_quantity" @click="addPackSize">Add</button>
            </div>
          </div>
          <div v-if="packForm.error" class="text-danger small">{{ packForm.error }}</div>
        </div>
        <div class="d-flex justify-content-end mt-3" style="gap:8px;">
          <button v-if="canEditRecipes && ['draft','rejected'].includes(selectedBom.status)" class="btn btn-primary btn-sm" @click="bomAction('submit')">Submit for approval</button>
          <button v-if="selectedBom.status === 'pending_approval'" class="btn btn-primary btn-sm" @click="bomAction('approve')">Approve</button>
          <button v-if="selectedBom.status === 'pending_approval'" class="btn btn-outline-danger btn-sm" @click="bomAction('reject')">Reject</button>
          <button v-if="canEditRecipes" class="btn btn-outline-secondary btn-sm" :disabled="!canEditInPlace"
            :title="canEditInPlace ? '' : 'Approved or used versions can\'t change — use Edit (new version).'"
            @click="editBomInPlace = true; openModal('newBom', selectedBom)">Edit this version</button>
          <button v-if="canEditRecipes" class="btn btn-outline-secondary btn-sm" @click="editBomInPlace = false; openModal('newBom', selectedBom)">Edit (new version)</button>
          <button v-if="canDeleteRecipes" class="btn btn-outline-danger btn-sm" :disabled="Number(selectedBom.production_orders_count) > 0"
            :title="Number(selectedBom.production_orders_count) > 0 ? `Used by ${selectedBom.production_orders_count} production order(s) — kept for traceability. Edit to make a new version instead.` : ''"
            @click="$refs.deleteBomModal.show()">Delete</button>
          <button v-if="selectedBom.status === 'approved' || (selectedBom.status === 'superseded' && selectedBom.approved_at)" class="btn btn-primary btn-sm" @click="$refs.newOrderModal.show(); $refs.viewBomModal.hide()">Create Order from BOM</button>
        </div>
      </div>
    </b-modal>

    <!-- Delete a recipe version (server refuses versions used by production orders) -->
    <b-modal ref="deleteBomModal" title="Delete recipe version" centered hide-footer>
      <template v-if="selectedBom">
        <p class="mb-2">Delete <strong>{{ selectedBom.name }}</strong> (v{{ selectedBom.version }})?</p>
        <p v-if="selectedBom.status === 'approved' && selectedBom.is_active" class="text-danger small mb-2">
          This is the recipe's current approved version. Until another version is approved, no production orders can be made from this recipe.
        </p>
        <p class="text-muted small">If it's the only version, the recipe itself is removed. This can't be undone.</p>
        <div class="d-flex justify-content-end" style="gap:8px;">
          <button class="btn btn-outline-secondary" @click="$refs.deleteBomModal.hide()">Cancel</button>
          <button class="btn btn-danger" :disabled="deletingBom" @click="deleteSelectedBom">
            <span v-if="deletingBom" class="spinner-border spinner-border-sm mr-1"></span>Delete
          </button>
        </div>
      </template>
    </b-modal>

    <!-- Approve Modal -->
    <b-modal ref="approveModal" title="Approve Production Order" @ok="approveOrder">
      <p v-if="activeOrder">
        Approve <strong>{{ activeOrder.order_number }}</strong>?
      </p>
      <p class="text-muted">Stock availability will be re-verified. Someone other than the person who created the order should approve it.</p>
    </b-modal>

    <!-- Admin approving their own order/recipe (segregation of duties on) -->
    <b-modal ref="selfApprovalModal" title="Approve your own work?" ok-title="Approve" @ok.prevent="submitSelfApproval">
      <p>You created this {{ selfApproval.kind === 'bom' ? 'recipe' : 'order' }} yourself. Normally a second person approves it.</p>
      <p class="text-muted">As an admin you can approve it anyway. Say why, for example "only one manager on shift". Your reason is saved with the approval.</p>
      <div class="form-group mb-0">
        <label for="self-approval-reason">Reason</label>
        <textarea id="self-approval-reason" v-model="selfApproval.reason" class="form-control" rows="3" maxlength="500"></textarea>
        <div v-if="selfApproval.error" class="text-danger small mt-1">{{ selfApproval.error }}</div>
      </div>
    </b-modal>

    <!-- Cancel Modal -->
    <b-modal ref="cancelModal" title="Cancel Order" @ok="cancelOrder" ok-variant="danger" ok-title="Cancel Order">
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <textarea class="form-control" v-model="cancelForm.reason" rows="3" placeholder="Enter reason for cancellation..." />
      </div>
    </b-modal>

    <!-- Reason modal: return to draft / quality release / reverse -->
    <b-modal ref="reasonModal" :title="reasonAction.title" @ok.prevent="submitReason" :ok-title="reasonAction.ok" :ok-variant="reasonAction.variant">
      <p class="small text-muted">{{ reasonAction.help }}</p>
      <div v-if="reasonAction.key === 'release'" class="btn-group btn-group-toggle mb-3">
        <button type="button" class="btn" :class="reasonForm.decision === 'release' ? 'btn-primary' : 'btn-outline-primary'" @click="reasonForm.decision = 'release'">Release</button>
        <button type="button" class="btn" :class="reasonForm.decision === 'reject' ? 'btn-danger' : 'btn-outline-danger'" @click="reasonForm.decision = 'reject'">Reject</button>
      </div>
      <div class="form-group">
        <label>Reason <span v-if="reasonAction.key !== 'release' || reasonForm.decision === 'reject'" class="text-danger">*</span></label>
        <textarea class="form-control" rows="3" v-model="reasonForm.reason"></textarea>
      </div>
      <div v-if="reasonError" class="alert alert-danger mb-0">{{ reasonError }}</div>
    </b-modal>

    <!-- Complete Stage Modal -->
    <b-modal ref="completeStageModal" :title="activeStage ? 'Complete: ' + (activeStage.bom_stage && activeStage.bom_stage.name) : 'Complete Stage'"
      @ok="completeStage" ok-title="Mark Complete">
      <div v-if="activeStage">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Output Quantity</label>
            <input type="number" class="form-control" v-model.number="stageForm.output_quantity" min="0" step="any" />
          </div>
          <div class="form-group col-md-6">
            <label>Wastage at this stage</label>
            <input type="number" class="form-control" v-model.number="stageForm.actual_wastage" min="0" step="any" />
          </div>
        </div>
        <div class="form-group">
          <label>Notes</label>
          <input type="text" class="form-control" v-model="stageForm.notes" placeholder="Optional..." />
        </div>
        <div v-if="activeStage.bom_stage && activeStage.bom_stage.wastage_tracking !== 'none'" class="mt-2 p-2 bg-light rounded">
          <label class="mb-1 small font-weight-bold">Quick wastage record <span v-if="activeStage.bom_stage.wastage_tracking === 'required'" class="text-danger">*</span></label>
          <div class="form-row mb-1">
            <div class="col-5">
              <select class="form-control" v-model="stageForm.wastage.product_id">
                <option value="">Select material...</option>
                <option v-for="u in activeOrder && activeOrder.material_usages" :key="u.product_id" :value="u.product_id">
                  {{ u.product && u.product.product }}
                </option>
              </select>
            </div>
            <div class="col-3"><input type="number" class="form-control" v-model.number="stageForm.wastage.quantity" placeholder="Qty" min="0" step="any" /></div>
            <div class="col-4"><input type="text" class="form-control" v-model="stageForm.wastage.reason" placeholder="Reason..." /></div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Complete Order Modal -->
    <b-modal ref="completeModal" title="Complete Production Order" @ok.prevent="completeOrder" ok-title="Complete & Record" :ok-disabled="completing" size="lg">
      <div v-if="activeOrder">
        <div class="form-group">
          <label>Actual Output <span class="text-danger">*</span></label>
          <div class="input-group">
            <input type="number" class="form-control" v-model.number="completeForm.actual_batch_size" min="0.0001" step="any" />
            <div class="input-group-append"><span class="input-group-text">{{ activeOrder.bom && activeOrder.bom.batch_unit }}</span></div>
          </div>
          <small class="text-muted">Planned: {{ activeOrder.planned_batch_size }}</small>
        </div>

        <div v-if="completeForm.actual_batch_size && completeForm.actual_batch_size < activeOrder.planned_batch_size" class="custom-control custom-switch mb-3">
          <input type="checkbox" class="custom-control-input" id="createRemSwitch" v-model="completeForm.create_remainder" />
          <label class="custom-control-label" for="createRemSwitch">
            Create remainder order for {{ (activeOrder.planned_batch_size - completeForm.actual_batch_size).toFixed(2) }} {{ activeOrder.bom && activeOrder.bom.batch_unit }}
          </label>
        </div>

        <label class="mb-1">Actual Material Quantities</label>
        <div class="material-table">
          <div class="material-row" v-for="m in completeForm.materials" :key="m.usage_id">
            <span class="material-name">{{ m.product_name }} <span v-if="m.track_lots" class="badge badge-secondary">lots: earliest expiry first</span></span>
            <span class="text-muted">planned: {{ m.planned_quantity }} {{ m.unit }}</span>
            <div class="input-group ml-auto" style="width:170px">
              <input type="number" class="form-control" v-model.number="m.actual_quantity" min="0" step="any" />
              <div class="input-group-append"><span class="input-group-text">{{ m.unit }}</span></div>
            </div>
          </div>
        </div>
        <small class="text-muted d-block mt-1">Enter what was really used, including anything spilt or wasted. Wastage you recorded is part of these quantities, not extra.</small>
        <!-- Conversion cost is never added silently: the user chooses. -->
        <div v-if="conversionPerUnit > 0" class="border rounded p-3 mt-3">
          <label class="mb-2 d-block">Cost of this batch</label>
          <div class="custom-control custom-radio mb-1">
            <input id="convNo" v-model="completeForm.include_conversion" type="radio" :value="false" class="custom-control-input">
            <label class="custom-control-label" for="convNo">Ingredients only<span v-if="ingredientsPerUnit !== null"> — about {{ formatCurrency(ingredientsPerUnit) }} / {{ activeOrder.bom.batch_unit }}</span></label>
          </div>
          <div class="custom-control custom-radio">
            <input id="convYes" v-model="completeForm.include_conversion" type="radio" :value="true" class="custom-control-input">
            <label class="custom-control-label" for="convYes">Ingredients + conversion cost ({{ formatCurrency(conversionPerUnit) }} / {{ activeOrder.bom.batch_unit }} for labour, power…)<span v-if="ingredientsPerUnit !== null"> — about {{ formatCurrency(ingredientsPerUnit + conversionPerUnit) }} / {{ activeOrder.bom.batch_unit }}</span></label>
          </div>
        </div>

        <div v-if="orderHasPackSizes" class="alert alert-secondary py-2 mt-3 mb-0 small">
          <i class="bi bi-box-seam mr-1"></i>The whole batch goes into stock as <strong>{{ activeOrder.output_product && activeOrder.output_product.product }}</strong>.
          Then pack it into its pack sizes with <strong>Pack</strong> on this order (or skip packing).
        </div>
        <div v-if="activeOrder.output_product && activeOrder.output_product.track_lots" class="form-row mt-3">
          <div class="form-group col-md-6">
            <label>Batch expiry date</label>
            <date-range-picker v-model="completeForm.expiryRange" :single-date-picker="true" :ranges="false" :auto-apply="true">
              <template #input="picker"><span>{{ picker.startDate ? formatPickerDate(picker.startDate) : 'No expiry' }}</span></template>
            </date-range-picker>
          </div>
        </div>
        <div v-if="completeError" class="alert alert-danger mt-3 mb-0">{{ completeError }}</div>
      </div>
    </b-modal>

    <!-- Wastage Record Modal -->
    <b-modal ref="wastageModal" title="Record Wastage" @ok="saveWastage" ok-title="Record">
      <div v-if="activeOrder">
        <div class="form-group">
          <label>Material Wasted <span class="text-danger">*</span></label>
          <select class="form-control" v-model="wastageForm.product_id">
            <option value="">— Select —</option>
            <option v-for="u in activeOrder.material_usages" :key="u.product_id" :value="u.product_id">
              {{ u.product && u.product.product }}
            </option>
            <option v-if="activeOrder.output_product" :value="activeOrder.output_product_id">{{ activeOrder.output_product.product }} (finished output rejected)</option>
          </select>
        </div>
        <div class="form-row">
          <div class="form-group col-md-5">
            <label>Quantity <span class="text-danger">*</span></label>
            <input type="number" class="form-control" v-model.number="wastageForm.quantity" min="0.0001" step="any" />
          </div>
          <div class="form-group col-md-3">
            <label>Unit</label>
            <input type="text" class="form-control" v-model="wastageForm.unit" placeholder="kg, g..." />
          </div>
        </div>
        <div class="form-group">
          <label>Reason <span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="wastageForm.reason" placeholder="e.g. spillage, overcooked..." />
        </div>
      </div>
    </b-modal>

    <!-- Stock Ledger Modal -->
    <b-modal ref="stockLedgerModal" title="Stock Ledger" hide-footer size="xl" @shown="onStockLedgerShown">
      <div class="filter-bar mb-3" style="flex-wrap:wrap; gap:8px;">
        <div class="position-relative" style="flex:1; min-width:180px;">
          <input
            type="text"
            class="form-control"
            v-model="stockLedger.productSearch"
            @input="searchLedgerProduct"
            placeholder="Search product..."
            autocomplete="off"
            data-ledger-search
          />
          <div v-if="stockLedger.suggestions.length" class="bom-typeahead" style="z-index:2100;">
            <button
              v-for="p in stockLedger.suggestions"
              :key="p.id"
              type="button"
              class="bom-typeahead-item"
              @mousedown.prevent="selectLedgerProduct(p)"
            >
              <span>{{ p.product }}</span>
              <small :class="p.remaining_units <= (p.minimum_stock || 0) ? 'text-danger' : 'text-muted'" class="ml-auto">
                {{ p.remaining_units }} {{ p.measuring_unit }}
              </small>
            </button>
          </div>
        </div>
        <select class="form-control" v-model="stockLedger.entryType" @change="loadStockLedger" style="width:180px;">
          <option value="">All Movement Types</option>
          <option value="purchase">Purchase</option>
          <option value="production_consumption">Consumed in Production</option>
          <option value="production_output">Production Output</option>
          <option value="sale">Sale</option>
          <option value="wastage">Wastage</option>
          <option value="adjustment">Adjustment</option>
        </select>
        <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="stockLedgerDateRangePicker" style="width:220px;">
          <template #input="picker">
            <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
            <span v-else class="text-muted small"><i class="bi bi-calendar-event mr-1"></i>Date range</span>
          </template>
        </date-range-picker>
      </div>

      <!-- Selected product balance -->
      <div v-if="stockLedger.selectedProduct" class="d-flex justify-content-between align-items-center p-2 mb-3 rounded" style="background:#f8f9fa; border:1px solid #e9ecef;">
        <div>
          <strong>{{ stockLedger.selectedProduct.product }}</strong>
          <span class="text-muted ml-2">Current balance:</span>
          <span class="font-weight-bold ml-1" :class="stockLedger.selectedProduct.remaining_units <= (stockLedger.selectedProduct.minimum_stock || 0) ? 'text-danger' : 'text-dark'">
            {{ stockLedger.selectedProduct.remaining_units }} {{ stockLedger.selectedProduct.measuring_unit }}
          </span>
          <small v-if="stockLedger.selectedProduct.minimum_stock" class="text-muted ml-2">(min: {{ stockLedger.selectedProduct.minimum_stock }})</small>
        </div>
        <button class="btn btn-sm btn-link text-muted" @click="clearStockLedger">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Ledger table -->
      <div v-if="stockLedger.loading" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary"></div>
      </div>
      <div v-else-if="stockLedger.entries.length">
        <table class="table table-sm table-striped mb-2">
          <thead class="thead-light">
            <tr>
              <th>Type</th>
              <th>Change</th>
              <th>Balance After</th>
              <th>Unit Cost</th>
              <th>Notes</th>
              <th>By</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in stockLedger.entries" :key="entry.id">
              <td>
                <span :class="ledgerEntryBadge(entry.entry_type)" class="badge badge-sm">
                  {{ formatEntryType(entry.entry_type) }}
                </span>
              </td>
              <td>
                <span :class="entry.quantity > 0 ? 'text-dark' : 'text-danger'" class="font-weight-bold">
                  {{ entry.quantity > 0 ? '+' : '' }}{{ entry.quantity }}
                </span>
              </td>
              <td>{{ entry.balance_after }}</td>
              <td><small>{{ entry.unit_cost ? formatCurrency(entry.unit_cost) : '—' }}</small></td>
              <td><small class="text-muted">{{ entry.notes || '—' }}</small></td>
              <td><small>{{ entry.actor && entry.actor.name }}</small></td>
              <td><small class="text-muted">{{ formatDate(entry.created_at) }}</small></td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">{{ stockLedger.total }} entries</small>
          <b-pagination
            v-model="stockLedger.currentPage"
            :total-rows="stockLedger.total"
            :per-page="stockLedger.perPage"
            size="sm"
            @change="loadStockLedger"
          />
        </div>
      </div>
      <div v-else-if="stockLedger.selectedProduct" class="text-center text-muted py-4">
        No movements found for the selected filters.
      </div>
      <div v-else class="text-center py-5">
        <i class="bi bi-journal-text" style="font-size:2.5rem; opacity:.25;"></i>
        <p class="text-muted mt-2">Search for a product above to view its movement history</p>
      </div>
    </b-modal>

    <!-- Low Stock Modal -->
    <b-modal ref="lowStockModal" title="Low Stock Materials" hide-footer size="md">
      <table class="table table-sm">
        <thead><tr><th>Material</th><th>Stock</th><th>Minimum</th><th>Unit</th></tr></thead>
        <tbody>
          <tr v-for="p in lowStockMaterials" :key="p.id">
            <td>{{ p.product }}</td>
            <td class="text-danger font-weight-bold">{{ p.remaining_units }}</td>
            <td class="text-muted">{{ p.minimum_stock }}</td>
            <td class="text-muted">{{ p.measuring_unit }}</td>
          </tr>
        </tbody>
      </table>
      <div class="text-right mt-2">
        <a href="/inventory" class="btn btn-sm btn-outline-primary">Go to Inventory</a>
      </div>
    </b-modal>

    <!-- Analytics Modal -->
    <b-modal ref="analyticsModal" title="Production Analytics" hide-footer size="xl">
      <div v-if="kpis.avg_efficiency != null">
        <div class="kpi-strip mb-3">
          <div class="kpi-card">
            <div class="kpi-value">{{ kpis.completed_orders }}</div>
            <div class="kpi-label">Completed</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value" :class="kpis.avg_efficiency >= 90 ? 'text-dark' : 'text-danger'">{{ kpis.avg_efficiency }}%</div>
            <div class="kpi-label">Avg Efficiency</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value" :class="kpis.cost_variance > 0 ? 'text-danger' : 'text-dark'">{{ formatCurrency(kpis.cost_variance) }}</div>
            <div class="kpi-label">Cost Variance</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-value text-danger">{{ formatCurrency(kpis.total_wastage_value) }}</div>
            <div class="kpi-label">Wastage Value</div>
          </div>
        </div>
        <table class="table table-sm table-striped">
          <thead class="thead-light">
            <tr><th>Order</th><th>Product</th><th>Output</th><th>Cost</th><th>Unit Cost</th><th>Efficiency</th><th>Margin</th></tr>
          </thead>
          <tbody>
            <tr v-for="b in costTrend" :key="b.order_number">
              <td><small class="font-weight-bold text-primary">{{ b.order_number }}</small></td>
              <td><small>{{ b.product }}</small></td>
              <td><small>{{ b.actual_batch_size }}</small></td>
              <td><small :class="b.actual_cost > b.planned_cost ? 'text-danger' : 'text-dark'">{{ formatCurrency(b.actual_cost) }}</small></td>
              <td><small>{{ formatCurrency(b.unit_cost) }}</small></td>
              <td><span :class="b.efficiency >= 90 ? 'badge badge-primary' : b.efficiency >= 75 ? 'badge badge-secondary' : 'badge badge-danger'" class="badge-sm">{{ b.efficiency }}%</span></td>
              <td><small :class="b.gross_margin_pct >= 30 ? 'text-dark' : 'text-danger'">{{ b.gross_margin_pct != null ? b.gross_margin_pct + '%' : '—' }}</small></td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- Wastage Log Modal -->
    <b-modal ref="wastageLogModal" title="Wastage Log" hide-footer size="lg">
      <div class="filter-bar mb-2">
        <select class="form-control" v-model="wastageLog.type" @change="loadWastageLog" style="width:140px">
          <option value="">All Types</option>
          <option value="normal">Normal</option>
          <option value="abnormal">Abnormal</option>
        </select>
      </div>
      <table class="table table-sm table-striped">
        <thead class="thead-light"><tr><th>Type</th><th>Product</th><th>Qty</th><th>Reason</th><th>Order</th><th>Date</th></tr></thead>
        <tbody>
          <tr v-for="w in wastageLog.items" :key="w.id">
            <td><span :class="w.wastage_type === 'abnormal' ? 'badge badge-danger' : 'badge badge-secondary'">{{ w.wastage_type }}</span></td>
            <td><small>{{ w.product && w.product.product }}</small></td>
            <td><small>{{ w.quantity }} {{ w.unit }}</small></td>
            <td><small>{{ w.reason }}</small></td>
            <td><small class="text-primary" style="cursor:pointer" @click="openOrderDetail(w.production_order_id); $refs.wastageLogModal.hide()">{{ w.production_order && w.production_order.order_number }}</small></td>
            <td><small class="text-muted">{{ formatDate(w.created_at) }}</small></td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Packing history: every packing session -->
    <b-modal ref="packingHistoryModal" title="Packing history" size="lg" hide-footer>
      <input v-model="packingHistory.search" type="text" class="form-control mb-3" placeholder="Search by PKG number or product" @input="debouncePackingHistory">
      <div v-if="packingHistory.loading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>
      <div v-else-if="!packingHistory.rows.length" class="text-muted">No packing yet.</div>
      <div v-else class="list-group">
        <div v-for="r in packingHistory.rows" :key="r.id" class="list-group-item">
          <div class="d-flex flex-wrap align-items-center" style="gap:8px;">
            <strong>{{ r.number }}</strong>
            <span class="text-muted small">{{ new Date(r.created_at).toLocaleString() }} · {{ r.created_by && r.created_by.name }}</span>
            <span v-if="r.production_order" class="badge badge-secondary">{{ r.production_order.order_number }}</span>
            <span v-if="r.reversed_at" class="badge badge-danger">Reversed</span>
            <button v-else-if="packingHistory.reversing !== r.id" class="btn btn-link btn-sm text-danger p-0 ml-auto" @click="packingHistory.reversing = r.id; packingHistory.reason = ''">Reverse</button>
          </div>
          <div class="small mt-1">
            {{ Number(r.source_quantity) }} {{ r.source_product && r.source_product.measuring_unit }} of {{ r.source_product && r.source_product.product }}
            → <span v-for="(l, i) in r.lines" :key="l.id">{{ i ? ', ' : '' }}{{ Number(l.packs) }} × {{ l.product && l.product.product }}</span>
            <span class="text-muted"> · cost {{ formatCurrency(r.total_cost) }}</span>
            <span v-if="r.notes" class="text-muted"> · {{ r.notes }}</span>
          </div>
          <div v-if="r.reversed_at && r.reversal_reason" class="small text-muted">Reversed: {{ r.reversal_reason }}</div>
          <div v-if="packingHistory.reversing === r.id" class="d-flex mt-2" style="gap:8px;">
            <input v-model="packingHistory.reason" type="text" class="form-control" placeholder="Why reverse it?">
            <button class="btn btn-danger" :disabled="!packingHistory.reason.trim()" @click="reversePacking(r)">Reverse</button>
            <button class="btn btn-outline-secondary" @click="packingHistory.reversing = null">Cancel</button>
          </div>
        </div>
      </div>
    </b-modal>

    <repack-modal ref="repackModal" @done="refreshActiveOrder(); loadOrders && loadOrders()"></repack-modal>
  </div>
</template>

<script>
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import DateTimeField from './DateTimeField.vue';
import BomFormInline from './BomFormInline.vue';
import LotsPanel from './LotsPanel.vue';
import TraceabilityPanel from './TraceabilityPanel.vue';
import DeliveryNotesPanel from './DeliveryNotesPanel.vue';
import ManufacturingReports from './ManufacturingReports.vue';
import InventorySettingsPanel from './InventorySettingsPanel.vue';
import ManufacturingGuide from './ManufacturingGuide.vue';
import { apiError } from './mfgHelpers';

// Tabs that show recipe contents: hidden without view-recipes / edit-recipes.
const RECIPE_TABS = ['boms', 'grouped'];

export default {
  name: 'ManufacturingHub',
  components: { BomFormInline, DateRangePicker, DateTimeField, LotsPanel, TraceabilityPanel, DeliveryNotesPanel, ManufacturingReports, InventorySettingsPanel, ManufacturingGuide },

  data() {
    return {
      selfApproval: { kind: 'order', reason: '', error: '' },
      currentTab: 'dashboard',
      tabs: [
        { key: 'dashboard', label: 'Overview',  icon: 'bi bi-house' },
        { key: 'orders',    label: 'Orders',    icon: 'bi bi-list-check' },
        { key: 'boms',      label: 'Recipes',   icon: 'bi bi-file-earmark-text' },
        { key: 'grouped',   label: 'Grouped items', icon: 'bi bi-collection' },
        { key: 'lots',      label: 'Lots & QC', icon: 'bi bi-upc-scan' },
        { key: 'trace',     label: 'Traceability', icon: 'bi bi-diagram-3' },
        { key: 'delivery',  label: 'Deliveries', icon: 'bi bi-truck' },
        { key: 'reports',   label: 'Reports',   icon: 'bi bi-graph-up' },
        { key: 'settings',  label: 'Settings',  icon: 'bi bi-gear' },
        { key: 'more',      label: 'More',      icon: 'bi bi-grid' },
      ],
      visited: {},
      sampleData: { available: false, loaded: false, busy: false, error: null, kind: 'feed', kinds: { feed: false, bakery: false } },
      completeError: null,
      completing: false,
      reasonAction: { key: '', stage: null, title: '', ok: '', variant: 'primary', help: '' },
      reasonForm: { reason: '', decision: 'release' },
      reasonError: null,

      // Dashboard data
      dashLoading: true,
      kpis: {},
      activeOrders: [],
      costTrend: [],
      wastageStats: {},
      lowStockMaterials: [],

      // Orders tab
      ordersLoading: false,
      orders: [],
      orderFilters: { search: '', status: '' },
      orderPagination: { currentPage: 1, perPage: 20, total: 0 },
      _orderDebounce: null,

      // BOMs tab
      bomsLoading: false,
      boms: [],
      bomSearch: '',
      _bomDebounce: null,
      selectedBom: null,
      editingBom: null,
      editBomInPlace: false,
      packingPlanned: false,
      packingHistory: { rows: [], search: '', loading: false, reversing: null, reason: '' },
      deletingBom: false,

      // Slide-over order detail
      activeOrderId: null,
      activeOrder: null,

      // New order modal state
      newOrder: {
        productSearch: '',
        productSuggestions: [],
        bomVersions: [],
        bom_id: '',
        selectedBom: null,
        selectedGroupedInventory: null,    // full GI object once selected
        loadingBoms: false,                // spinner while fetching BOM versions
        generatingBom: false,              // spinner while auto-generating
        showNoBomPrompt: false,            // GI has items but no BOM
        showEmptyPrompt: false,            // GI has no items at all
        showVersionPicker: false,          // expand multi-version selector
        justGenerated: false,              // show success flash after auto-gen
        ingredientCount: 0,                // for "found N ingredients" text
        ingredientPreview: [],             // first 4 ingredients for preview pills
        bundleSuggestions: [],             // counter bundles matching the search
        bundle: null,                      // chosen bundle: its items become a recipe for a finished product
        bundleProductSearch: '', bundleProductSuggestions: [], bundleOutput: null,
        planned_batch_size: null,
        packSizes: [],                     // recipe's pack sizes, each with .planned bags
        planned_start_at: null,
        notes: '',
        availability: null,
        checking: false,
        saving: false,
        _avTimer: null,
      },

      // Action modals
      cancelForm: { reason: '' },
      stageForm: { output_quantity: null, actual_wastage: 0, notes: '', wastage: { product_id: '', quantity: 0, unit: '', reason: '' } },
      completeForm: { actual_batch_size: null, materials: [], create_remainder: false, include_conversion: false, expiryRange: { startDate: null, endDate: null }, packs: [], leftover: 'keep' },
      packForm: { productSearch: '', productSuggestions: [], product_id: null, content_quantity: null, bagSearch: '', bagSuggestions: [], packaging_product_id: null, kit: [], error: null },
      wastageForm: { product_id: '', quantity: null, unit: '', reason: '', production_order_stage_id: null },
      activeStage: null,
      wastageLog: { items: [], type: '' },
      stockLedger: {
        productSearch: '', suggestions: [], selectedProduct: null,
        entryType: '', from: '', to: '',
        entries: [], loading: false,
        currentPage: 1, perPage: 30, total: 0,
        _timer: null,
      },
    };
  },

  computed: {
    canViewRecipes() { return this.hasPermission('view-recipes') || this.hasPermission('edit-recipes'); },
    canEditRecipes() { return this.hasPermission('edit-recipes'); },
    canEditInPlace() {
      const b = this.selectedBom;
      return !!b && ['draft', 'pending_approval', 'rejected'].includes(b.status) && !(Number(b.production_orders_count) > 0);
    },
    canDeleteRecipes() { return this.canEditRecipes && this.hasPermission('delete-bom'); },
    visibleTabs() {
      return this.canViewRecipes ? this.tabs : this.tabs.filter(t => !RECIPE_TABS.includes(t.key));
    },
    sampleKindsLeft() {
      return ['feed', 'bakery'].filter(k => !this.sampleData.kinds[k]);
    },
    stockLedgerDateRangePicker: {
      get() {
        return {
          startDate: this.stockLedger.from ? new Date(this.stockLedger.from) : null,
          endDate: this.stockLedger.to ? new Date(this.stockLedger.to) : null,
        };
      },
      set(val) {
        this.stockLedger.from = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
        this.stockLedger.to = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : '';
        this.loadStockLedger();
      },
    },
    today() {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    },
    kpiCards() {
      const k = this.kpis;
      return [
        { label: 'Orders', value: k.total_orders ?? 0, color: 'text-dark', action: () => { this.currentTab = 'orders'; } },
        // { label: 'In Progress', value: k.in_progress_orders ?? 0, color: 'text-dark' },
        { label: 'Avg Efficiency', value: (k.avg_efficiency ?? 0) + '%', color: k.avg_efficiency >= 90 ? 'text-dark' : 'text-danger' },
        { label: 'Cost Variance', value: this.formatCurrency(k.cost_variance ?? 0), color: (k.cost_variance ?? 0) > 0 ? 'text-danger' : 'text-dark' },
        { label: 'Wastage', value: this.formatCurrency(k.total_wastage_value ?? 0), color: 'text-danger', action: () => this.openModal('wastageLog') },
      ];
    },
    maxBatchCost() {
      return Math.max(...this.costTrend.map(b => b.actual_cost || 0), 1);
    },
    allStagesComplete() {
      if (!this.activeOrder?.stages?.length) return true;
      return this.activeOrder.stages.every(s => ['completed', 'skipped'].includes(s.status));
    },
    nextPendingIndex() {
      if (!this.activeOrder?.stages) return -1;
      return this.activeOrder.stages.findIndex(s => s.status === 'pending');
    },
    selectedBomUsable() {
      const b = this.newOrder.selectedBom;
      // The active approved version, or an older approved one that was superseded (reuse).
      return !!b && ((b.is_active && (b.status === 'approved' || b.status === undefined)) || (b.status === 'superseded' && !!b.approved_at));
    },
    /** The new order's bag plan against its batch size (when packs use the batch unit). */
    bagPlanSummary() {
      const o = this.newOrder;
      const used = o.packSizes.filter(p => Number(p.planned) > 0);
      const unit = o.selectedBom && o.selectedBom.batch_unit;
      if (!used.length || !(o.planned_batch_size > 0) || used.some(p => p.content_unit !== unit)) return null;
      const fmt = n => Math.round(n * 1000) / 1000;
      const packed = used.reduce((t, p) => t + Number(p.planned) * Number(p.content_quantity), 0);
      const left = fmt(Number(o.planned_batch_size) - packed);
      if (left < 0) return { over: true, text: `The bags hold ${fmt(packed)} ${unit} but the batch is only ${fmt(o.planned_batch_size)} ${unit}.` };
      return { over: false, text: `Bags hold ${fmt(packed)} of ${fmt(o.planned_batch_size)} ${unit}` + (left > 0 ? `, ${left} ${unit} left loose.` : '.') };
    },
    /** The active order's planned bags, named from the recipe's pack sizes. */
    plannedPackLines() {
      const o = this.activeOrder;
      const sizes = (o && o.bom && o.bom.grouped_inventory && o.bom.grouped_inventory.pack_sizes) || [];
      return ((o && o.planned_packs) || []).map(l => {
        const ps = sizes.find(s => s.id === l.pack_size_id);
        return ps ? { id: ps.id, quantity: l.quantity, name: ps.product && ps.product.product, content: `${Number(ps.content_quantity)} ${ps.content_unit}` } : null;
      }).filter(Boolean);
    },
    /** Ingredient quantities as entered, summed per unit. */
    bomMissingReasons() {
      return ((this.selectedBom && this.selectedBom.stages) || []).flatMap(st => st.materials || [])
        .filter(m => this.bomLineValue(m) === null).map(m => m.value_reason || `${m.product && m.product.product}: no buying price or unit mismatch`);
    },
    bomQtyText() {
      const byUnit = {};
      ((this.selectedBom && this.selectedBom.stages) || []).forEach(st => (st.materials || []).forEach(m => {
        const u = (m.unit || '').trim() || 'units';
        byUnit[u] = (byUnit[u] || 0) + Number(m.quantity || 0);
      }));
      return Object.entries(byUnit).filter(([, q]) => q > 0).map(([u, q]) => `${Math.round(q * 1000) / 1000} ${u}`).join(' + ');
    },
    bomTotals() {
      let total = 0, missing = 0, qty = 0;
      const unit = this.selectedBom && this.selectedBom.batch_unit;
      ((this.selectedBom && this.selectedBom.stages) || []).forEach(st => (st.materials || []).forEach(m => {
        const v = this.bomLineValue(m);
        if (v === null) missing++; else total += v;
        const f = this.unitFactorBetween(m.unit, unit);
        if (f !== null) qty += Number(m.quantity || 0) * f;
      }));
      return { total, missing, qty };
    },
    conversionPerUnit() {
      return Number((this.activeOrder && this.activeOrder.bom && this.activeOrder.bom.conversion_cost_per_unit) || 0);
    },
    /** Ingredients cost per batch unit from the planned material costs. */
    ingredientsPerUnit() {
      const o = this.activeOrder;
      const size = Number(this.completeForm.actual_batch_size || (o && o.planned_batch_size) || 0);
      if (!o || !size) return null;
      const total = (o.material_usages || []).reduce((t, u) => t + Number(u.planned_quantity || 0) * Number(u.unit_cost_at_time || 0), 0);
      return total / size;
    },
    orderHasPackSizes() {
      const o = this.activeOrder;
      return !!(o && o.bom && o.bom.grouped_inventory && (o.bom.grouped_inventory.pack_sizes || []).length);
    },
    bomPackSizes() {
      return (this.selectedBom && this.selectedBom.grouped_inventory && this.selectedBom.grouped_inventory.pack_sizes) || [];
    },
    /** Running total of the pack-out against the good output (when packs use the batch unit). */
    packOutSummary() {
      const f = this.completeForm;
      const unit = this.activeOrder && this.activeOrder.bom && this.activeOrder.bom.batch_unit;
      const used = f.packs.filter(p => Number(p.quantity) > 0);
      if (!used.length || used.some(p => p.content_unit !== unit)) return null;
      const packed = used.reduce((t, p) => t + Number(p.quantity) * p.content_quantity, 0);
      const total = Number(f.actual_batch_size) || 0;
      const left = Math.round((total - packed) * 1000) / 1000;
      const fmt = n => Math.round(n * 1000) / 1000;
      if (left < 0) return { over: true, left, text: `The packs hold ${fmt(packed)} ${unit} but only ${fmt(total)} ${unit} was produced.` };
      return { over: false, left, text: `Packed ${fmt(packed)} ${unit} of ${fmt(total)} ${unit}` + (left > 0 ? `, ${left} ${unit} left over.` : '.') };
    },
    canApprove() {
      // Can't approve your own order
      return this.activeOrder?.created_by?.id !== this.$page?.auth?.user?.id;
    },
  },

  mounted() {
    this.loadDashboard();
    axios.get('/api/manufacturing/settings').then(({ data }) => {
      this.sampleData.available = !!data.sample_data_available;
      this.sampleData.loaded = !!data.sample_data_loaded;
      this.sampleData.kinds = Object.assign({ feed: false, bakery: false }, data.sample_data_kinds_loaded || {});
      this.sampleData.kind = this.sampleKindsLeft[0] || 'feed';
    }).catch(() => {});
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },

    // ── Data loaders ──────────────────────────────────────────────────────────

    async loadDashboard() {
      this.dashLoading = true;
      try {
        const { data } = await axios.get('/api/manufacturing/analytics/dashboard', {
          params: { from: this.defaultFrom(), to: new Date().toISOString().slice(0, 10) },
        });
        this.kpis              = data.kpis;
        this.activeOrders      = data.active_orders;
        this.costTrend         = data.cost_trend;
        this.wastageStats      = data.wastage_stats;
        this.lowStockMaterials = data.low_stock_materials;
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Failed to load dashboard' });
      } finally {
        this.dashLoading = false;
      }
    },

    async loadOrders(page) {
      this.ordersLoading = true;
      try {
        const { data } = await axios.get('/api/manufacturing/production-orders', {
          params: { ...this.orderFilters, page: page || 1, per_page: this.orderPagination.perPage },
        });
        this.orders = data.data;
        this.orderPagination.total = data.total;
        this.orderPagination.currentPage = data.current_page;
      } finally {
        this.ordersLoading = false;
      }
    },

    debounceLoadOrders() {
      clearTimeout(this._orderDebounce);
      this._orderDebounce = setTimeout(() => this.loadOrders(), 350);
    },

    async loadBoms() {
      this.bomsLoading = true;
      try {
        const { data } = await axios.get('/api/manufacturing/boms', { params: { search: this.bomSearch } });
        this.boms = data.data || data;
      } finally {
        this.bomsLoading = false;
      }
    },

    debounceBoms() {
      clearTimeout(this._bomDebounce);
      this._bomDebounce = setTimeout(() => this.loadBoms(), 350);
    },

    async loadWastageLog() {
      try {
        const { data } = await axios.get('/api/manufacturing/production-wastage', {
          params: { wastage_type: this.wastageLog.type, per_page: 50 },
        });
        this.wastageLog.items = data.data || [];
      } catch (e) {}
    },

    // ── Slide-over ────────────────────────────────────────────────────────────

    async openOrderDetail(orderId) {
      this.activeOrderId = orderId;
      this.activeOrder = null;
      await this.refreshActiveOrder();
    },
    // debounceAvailability / fetchAvailability are called from the template and the
    // product picker but had been lost in an earlier rewrite, so the panel never loaded.
    debounceAvailability() {
      clearTimeout(this.newOrder._avTimer);
      this.newOrder._avTimer = setTimeout(() => this.loadAvailability(), 400);
    },
    fetchAvailability() { return this.loadAvailability(); },
    async loadAvailability() {
      const o = this.newOrder;
      if (!o.bom_id || !(o.planned_batch_size > 0)) { o.availability = null; return; }
      o.checking = true;
      try {
        // Multi-level: short materials that have their own recipe show what to make first.
        const { data } = await axios.post('/api/manufacturing/boms/explode', { bom_id: o.bom_id, batch_size: o.planned_batch_size });
        o.availability = data;
      } catch (e) { o.availability = null; } finally { o.checking = false; }
    },
        async createOrder() {
      this.newOrder.saving = true;
      try {
        const { data } = await axios.post('/api/manufacturing/production-orders', {
          bom_id: this.newOrder.bom_id,
          planned_batch_size: this.newOrder.planned_batch_size,
          planned_packs: this.newOrder.packSizes
            .filter(p => Number(p.planned) > 0)
            .map(p => ({ pack_size_id: p.id, quantity: Number(p.planned) })),
          planned_start_at: this.newOrder.planned_start_at,
          notes: this.newOrder.notes,
        });
        this.$toasts.push({ type: 'success', message: `Order ${data.order_number} created.` });
        this.$refs.newOrderModal.hide();
        await this.loadDashboard();
        this.openOrderDetail(data.id);
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' });
      } finally {
        this.newOrder.saving = false;
      }
    },

    closeOrderDetail() {
      this.activeOrderId = null;
      this.activeOrder = null;
    },

    async refreshActiveOrder() {
      if (!this.activeOrderId) return;
      try {
        const { data } = await axios.get(`/api/manufacturing/production-orders/${this.activeOrderId}`);
        this.activeOrder = data;
        // Keep the list behind the panel in step with the order's new status.
        const i = this.orders.findIndex(o => o.id === data.id);
        if (i > -1) this.$set(this.orders, i, { ...this.orders[i], ...data });
        this.completeForm.actual_batch_size = data.planned_batch_size;
        this.completeForm.materials = (data.material_usages || []).map(u => ({
          usage_id: u.id, product_name: u.product?.product, unit: u.unit,
          track_lots: !!(u.product && u.product.track_lots),
          planned_quantity: u.planned_quantity, actual_quantity: u.planned_quantity,
        }));
        // Packing is its own stage after completion (RepackModal), not part of it.
        this.completeForm.packs = [];
        this.completeForm.leftover = 'keep';
        this.completeForm.include_conversion = false;
        this.completeError = null;
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Failed to load order' });
      }
    },

    // ── Modal manager ─────────────────────────────────────────────────────────

    openModal(name, payload = null) {
      const map = {
        newOrder:      () => this.$refs.newOrderModal.show(),
        // With a recipe: edit it (saves a new draft version). Without: create one.
        newBom:        async () => {
          if (!this.canEditRecipes) return;
          let bom = payload;
          // The recipe list (and the view dialog until it finishes loading) has the stages
          // but not their materials; editing needs the full recipe or the form breaks.
          if (bom && (bom.stages || []).some(st => !Array.isArray(st.materials))) {
            try { bom = (await axios.get(`/api/manufacturing/boms/${bom.id}`)).data; }
            catch (e) { this.$toasts.push({ type: 'danger', message: apiError(e) }); return; }
          }
          this.editingBom = bom ? { ...bom, latest_version: Math.max(bom.version, ...this.boms.filter(b => b.grouped_inventory_id === bom.grouped_inventory_id).map(b => b.version)) } : null;
          if (bom) this.$refs.viewBomModal.hide();
          this.$refs.newBomModal.show();
        },
        viewBom:       () => { this.selectedBom = payload; this.$refs.viewBomModal.show(); axios.get(`/api/manufacturing/boms/${payload.id}`).then(({ data }) => { this.selectedBom = data; }).catch(() => this.$refs.viewBomModal.hide()); },
        approve:       () => this.$refs.approveModal.show(),
        cancel:        () => { this.cancelForm.reason = ''; this.$refs.cancelModal.show(); },
        completeStage: () => { this.activeStage = payload; this.stageForm = { output_quantity: null, actual_wastage: 0, notes: '', wastage: { product_id: '', quantity: 0, unit: '', reason: '' } }; this.$refs.completeStageModal.show(); },
        complete:      () => { this.completeError = null; this.$refs.completeModal.show(); },
        wastage:       () => { this.wastageForm = { production_order_id: this.activeOrderId, production_order_stage_id: payload?.id || null, product_id: '', quantity: null, unit: '', reason: '' }; this.$refs.wastageModal.show(); },
        lowStock:      () => this.$refs.lowStockModal.show(),
        analytics:     () => this.$refs.analyticsModal.show(),
        wastageLog:    () => { this.loadWastageLog(); this.$refs.wastageLogModal.show(); },
        stockLedger:   () => this.$refs.stockLedgerModal.show(),
      };
      map[name]?.();
    },

    // ── New order flow ────────────────────────────────────────────────────────

    async searchManufactured() {
      const q = (this.newOrder.productSearch || '').toLowerCase();
      if (q.length < 2) { this.newOrder.productSuggestions = []; this.newOrder.bundleSuggestions = []; return; }
      try {
        // Recipes (by recipe name, finished product or group) and counter bundles in one search.
        const { data } = await axios.get('/api/manufacturing/boms/order-sources', { params: { search: q } });
        if ((this.newOrder.productSearch || '').toLowerCase() !== q) return; // a newer keystroke won
        this.newOrder.productSuggestions = data.recipes || [];
        this.newOrder.bundleSuggestions  = data.bundles || [];
      } catch (e) {}
    },

    selectBundle(b) {
      this.resetNewOrderSelection();
      this.newOrder.productSearch = b.group_title;
      this.newOrder.bundle = b;
    },

    pickBundleOutput(p) {
      this.newOrder.bundleOutput = p;
      this.newOrder.bundleProductSearch = p.product;
      this.newOrder.bundleProductSuggestions = [];
    },

    async searchBundleOutput() {
      const q = (this.newOrder.bundleProductSearch || '').trim();
      this.newOrder.bundleOutput = null;
      if (q.length < 2) { this.newOrder.bundleProductSuggestions = []; return; }
      try {
        const { data } = await axios.get('/api/manufacturing/boms/output-products', { params: { search: q } });
        this.newOrder.bundleProductSuggestions = data;
      } catch (e) {}
    },

    async recipeFromBundle() {
      this.newOrder.generatingBom = true;
      try {
        const { data } = await axios.post('/api/manufacturing/boms/from-grouped-inventory', {
          grouped_inventory_id: this.newOrder.bundle.id,
          output_product_id: this.newOrder.bundleOutput.id,
        });
        this.newOrder.bundle = null;
        await this.selectManufacturedProduct({ ...data.grouped_inventory, preferBomId: data.bom.id });
        this.newOrder.justGenerated = true;
        setTimeout(() => { this.newOrder.justGenerated = false; }, 5000);
        this.$toasts.push({ type: 'success', message: data.message });
        this.loadBoms();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: apiError(e) });
      } finally {
        this.newOrder.generatingBom = false;
      }
    },

    resetNewOrderSelection() {
      Object.assign(this.newOrder, {
        productSuggestions: [], bundleSuggestions: [], bundle: null,
        bundleProductSearch: '', bundleProductSuggestions: [], bundleOutput: null,
        bom_id: '', selectedBom: null, selectedGroupedInventory: null, bomVersions: [], availability: null,
        showNoBomPrompt: false, showEmptyPrompt: false, showVersionPicker: false, justGenerated: false,
        ingredientCount: 0, ingredientPreview: [],
      });
    },


    bagShort(p) {
      return p.packaging_product && Number(p.planned) * Number(p.packaging_quantity || 1) > Number(p.packaging_product.remaining_units || 0);
    },

    async loadOrderPackSizes(groupedInventoryId) {
      this.newOrder.packSizes = [];
      if (!groupedInventoryId) return;
      try {
        const { data } = await axios.get('/api/manufacturing/pack-sizes', { params: { grouped_inventory_id: groupedInventoryId } });
        this.newOrder.packSizes = data.map(ps => ({ ...ps, planned: null }));
      } catch (e) { /* no pack sizes: the plan section stays hidden */ }
    },

    async selectManufacturedProduct(p) {
      // Clear all state from any prior selection
      this.newOrder.productSearch            = p.recipe ? p.recipe.name : p.group_title;
      this.newOrder.productSuggestions       = [];
      this.newOrder.bundleSuggestions        = [];
      this.newOrder.bundle                   = null;
      this.newOrder.bom_id                   = '';
      this.newOrder.selectedBom              = null;
      this.newOrder.selectedGroupedInventory = p;
      this.newOrder.bomVersions              = [];
      this.newOrder.availability             = null;
      this.newOrder.showNoBomPrompt          = false;
      this.newOrder.showEmptyPrompt          = false;
      this.newOrder.showVersionPicker        = false;
      this.newOrder.justGenerated            = false;
      this.newOrder.ingredientCount          = 0;
      this.newOrder.ingredientPreview        = [];
      this.newOrder.loadingBoms              = true;
      this.loadOrderPackSizes(p.id);

      try {
        const { data: versions } = await axios.get(`/api/manufacturing/boms/${p.id}/versions`);
        this.newOrder.bomVersions = versions;

        if (versions.length) {
          // ── Has BOM: auto-select active version ──────────────────────
          // Prefer the approved, active version; a draft can be shown but not used.
          // A version picked by its recipe name (or just created) wins; otherwise the approved, active one.
          const wanted = p.preferBomId || p.matched_bom_id;
          const active = (wanted && versions.find(b => b.id === wanted))
            || versions.find(b => b.status === 'approved' && b.is_active) || versions.find(b => b.is_active) || versions[0];
          this.newOrder.bom_id      = active.id;
          this.newOrder.selectedBom = active;
          if (this.newOrder.planned_batch_size) await this.fetchAvailability();
        } else {
          // ── No BOM: check whether GI has ingredients ──────────────────
          const items = p.grouped_items || p.groupedItems || [];
          // Previews are withheld without recipe access; items_count still says whether it has any.
          this.newOrder.ingredientCount = p.items_count ?? items.length;

          if (this.newOrder.ingredientCount > 0) {
            this.newOrder.ingredientPreview = items.slice(0, 4).map(i => ({
              product: i.product ? i.product.product : 'Unknown',
              count:   i.count,
              unit:    i.product ? (i.product.measuring_unit || 'pcs') : 'pcs',
            }));
            this.newOrder.showNoBomPrompt = true;
          } else {
            this.newOrder.showEmptyPrompt = true;
          }
        }
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Failed to load recipe. Please try again.' });
      } finally {
        this.newOrder.loadingBoms = false;
      }
    },

    async autoGenerateBom() {
      if (!this.newOrder.selectedGroupedInventory) return;
      this.newOrder.generatingBom = true;
      try {
        const { data } = await axios.post('/api/manufacturing/boms/from-grouped-inventory', {
          grouped_inventory_id: this.newOrder.selectedGroupedInventory.id,
        });
        const bom = data.bom;
        this.newOrder.showNoBomPrompt = false;
        this.newOrder.showEmptyPrompt = false;
        this.newOrder.justGenerated   = true;
        this.newOrder.bomVersions     = [bom];
        this.newOrder.bom_id          = bom.id;
        this.newOrder.selectedBom     = bom;
        // Auto-hide the success flash after 5s
        setTimeout(() => { this.newOrder.justGenerated = false; }, 5000);
        // Refresh Recipes tab list in background
        this.loadBoms();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed to create recipe.' });
      } finally {
        this.newOrder.generatingBom = false;
      }
    },

    async submitSelectedBom() {
      this.$set(this.newOrder, 'submittingBom', true);
      try {
        const { data } = await axios.patch(`/api/manufacturing/boms/${this.newOrder.selectedBom.id}/submit`);
        this.newOrder.selectedBom = { ...this.newOrder.selectedBom, ...data.bom };
        this.newOrder.bomVersions = this.newOrder.bomVersions.map(b => b.id === data.bom.id ? { ...b, ...data.bom } : b);
        this.$toasts.push({ type: 'success', message: 'Recipe submitted. A second person must approve it in Recipes.' });
        this.loadBoms();
      } catch (e) { this.$toasts.push({ type: 'error', message: apiError(e) }); }
      finally { this.$set(this.newOrder, 'submittingBom', false); }
    },

    goToRecipes() {
      this.$refs.newOrderModal.hide();
      this.currentTab = 'boms';
    },

    goToGroupedInventory() {
      this.$refs.newOrderModal.hide();
      window.location.href = '/group-inventory';
    },

    async onBomSelected() {
      this.newOrder.selectedBom       = this.newOrder.bomVersions.find(b => b.id === this.newOrder.bom_id) || null;
      this.newOrder.showVersionPicker  = false;
      this.newOrder.availability       = null;
      if (this.newOrder.planned_batch_size) await this.fetchAvailability();
    },

    resetNewOrder() {
      clearTimeout(this.newOrder._avTimer);
      this.newOrder = {
        productSearch: '', productSuggestions: [], bomVersions: [],
        bom_id: '', selectedBom: null, selectedGroupedInventory: null,
        loadingBoms: false, generatingBom: false,
        showNoBomPrompt: false, showEmptyPrompt: false,
        showVersionPicker: false, justGenerated: false,
        ingredientCount: 0, ingredientPreview: [],
        bundleSuggestions: [], bundle: null, bundleProductSearch: '', bundleProductSuggestions: [], bundleOutput: null,
        planned_batch_size: null, packSizes: [], planned_start_at: null, notes: '',
        availability: null, checking: false, saving: false, _avTimer: null,
      };
    },

    // ── Order actions ─────────────────────────────────────────────────────────

    async submitForApproval() {
      try {
        await axios.patch(`/api/manufacturing/production-orders/${this.activeOrderId}/submit`);
        this.$toasts.push({ type: 'success', message: 'Submitted for approval.' });
        this.refreshActiveOrder(); this.loadDashboard();
      } catch (e) { this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' }); }
    },

    async approveOrder() {
      try {
        await axios.patch(`/api/manufacturing/production-orders/${this.activeOrderId}/approve`);
        this.$toasts.push({ type: 'success', message: 'Order approved.' });
        this.refreshActiveOrder(); this.loadDashboard();
      } catch (e) {
        if (!this.askSelfApprovalReason(e, 'order')) this.$toasts.push({ type: 'error', message: apiError(e) });
      }
    },

    /** The server asks an admin approving their own work for a reason: open the reason dialog. */
    askSelfApprovalReason(e, kind) {
      if (!e?.response?.data?.errors?.self_approval_reason) return false;
      this.selfApproval = { kind, reason: '', error: '' };
      this.$refs.selfApprovalModal.show();
      return true;
    },

    async submitSelfApproval() {
      const reason = this.selfApproval.reason.trim();
      if (reason.length < 10) { this.selfApproval.error = 'Give a reason of at least 10 characters.'; return; }
      const url = this.selfApproval.kind === 'bom'
        ? `/api/manufacturing/boms/${this.selectedBom.id}/approve`
        : `/api/manufacturing/production-orders/${this.activeOrderId}/approve`;
      try {
        const { data } = await axios.patch(url, { self_approval_reason: reason });
        this.$refs.selfApprovalModal.hide();
        this.$toasts.push({ type: 'success', message: data.message || 'Approved.' });
        if (this.selfApproval.kind === 'bom') {
          this.selectedBom = (await axios.get(`/api/manufacturing/boms/${this.selectedBom.id}`)).data;
          this.loadBoms();
        } else {
          this.refreshActiveOrder(); this.loadDashboard();
        }
      } catch (e) { this.selfApproval.error = apiError(e); }
    },

    async startProduction() {
      try {
        await axios.patch(`/api/manufacturing/production-orders/${this.activeOrderId}/start`);
        this.$toasts.push({ type: 'success', message: 'Production started.' });
        this.refreshActiveOrder();
      } catch (e) { this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' }); }
    },

    async cancelOrder() {
      if (!this.cancelForm.reason) { this.$toasts.push({ type: 'warning', message: 'Enter a reason.' }); return; }
      try {
        await axios.patch(`/api/manufacturing/production-orders/${this.activeOrderId}/cancel`, { reason: this.cancelForm.reason });
        this.$toasts.push({ type: 'success', message: 'Order cancelled.' });
        this.closeOrderDetail(); this.loadDashboard();
      } catch (e) { this.$toasts.push({ type: 'danger', message: 'Failed.' }); }
    },

    async startStage(stage) {
      try {
        await axios.patch(`/api/manufacturing/production-stages/${stage.id}/start`);
        this.$toasts.push({ type: 'success', message: `"${stage.bom_stage?.name}" started.` });
        this.refreshActiveOrder();
      } catch (e) { this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' }); }
    },

    async completeStage() {
      try {
        await axios.patch(`/api/manufacturing/production-stages/${this.activeStage.id}/complete`, this.stageForm);
        this.$toasts.push({ type: 'success', message: 'Stage completed.' });
        this.refreshActiveOrder();
      } catch (e) { this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' }); }
    },

    async completeOrder() {
      this.completing = true; this.completeError = null;
      const f = this.completeForm;
      const payload = {
        actual_batch_size: f.actual_batch_size,
        create_remainder: !!f.create_remainder,
        materials: f.materials.map(m => ({ usage_id: m.usage_id, actual_quantity: m.actual_quantity, unit: m.unit })),
        include_conversion: !!f.include_conversion,
      };
      const packs = f.packs.filter(p => Number(p.quantity) > 0);
      if (packs.length) {
        payload.packs = packs.map(p => ({ pack_size_id: p.pack_size_id, quantity: Number(p.quantity) }));
        payload.leftover = f.leftover;
      }
      if (f.expiryRange && f.expiryRange.startDate) {
        payload.output_lot = { expiry_date: moment(f.expiryRange.startDate).format('YYYY-MM-DD') };
      }
      try {
        const { data } = await axios.patch(`/api/manufacturing/production-orders/${this.activeOrderId}/complete`, payload);
        this.$refs.completeModal.hide();
        this.$toasts.push({ type: 'success', message: data.message + (data.remainder_order ? ` Remainder order ${data.remainder_order.order_number} created.` : '') });
        this.refreshActiveOrder(); this.loadDashboard();
      } catch (e) { this.completeError = apiError(e); } finally { this.completing = false; }
    },

    openReason(key, stage = null) {
      const defs = {
        skipStage: { title: 'Skip stage', ok: 'Skip stage', variant: 'secondary', help: 'Use this when the stage did not happen for this batch. No stock moves; the reason is kept on the order timeline.' },
        returnToDraft: { title: 'Return to draft', ok: 'Return to draft', variant: 'secondary', help: 'The creator can then correct the order and resubmit it.' },
        release: { title: 'Quality release of finished batch', ok: 'Save decision', variant: 'primary', help: 'Released batches become sellable. Rejected batches stay out of sale; write them off with a stock adjustment if needed. Record quality checks on the batch in Lots & QC first.' },
        reverse: { title: 'Reverse completed production', ok: 'Reverse', variant: 'danger', help: 'Takes the finished output back out of stock, returns materials to their lots and reverses the journal. Only possible if none of the batch has been used or sold.' },
      };
      this.reasonAction = { key, stage, ...defs[key] };
      this.reasonForm = { reason: '', decision: 'release' };
      this.reasonError = null;
      this.$refs.reasonModal.show();
    },

    async submitReason() {
      const id = this.activeOrderId;
      const k = this.reasonAction.key;
      this.reasonError = null;
      try {
        if (k === 'returnToDraft') await axios.patch(`/api/manufacturing/production-orders/${id}/return-to-draft`, { reason: this.reasonForm.reason });
        if (k === 'release') await axios.patch(`/api/manufacturing/production-orders/${id}/release`, { decision: this.reasonForm.decision, reason: this.reasonForm.reason || null });
        if (k === 'reverse') await axios.patch(`/api/manufacturing/production-orders/${id}/reverse`, { reason: this.reasonForm.reason });
        if (k === 'skipStage') await axios.patch(`/api/manufacturing/production-stages/${this.reasonAction.stage.id}/skip`, { reason: this.reasonForm.reason });
        this.$refs.reasonModal.hide();
        this.$toasts.push({ type: 'success', message: 'Saved.' });
        this.refreshActiveOrder(); this.loadDashboard();
      } catch (e) { this.reasonError = apiError(e); }
    },

    searchPackProduct(which) {
      const f = this.packForm;
      const q = (which === 'bag' ? f.bagSearch : f.productSearch || '').trim();
      if (which === 'bag') f.packaging_product_id = null; else f.product_id = null;
      if (q.length < 2) { f[which === 'bag' ? 'bagSuggestions' : 'productSuggestions'] = []; return; }
      clearTimeout(f._timer);
      f._timer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/products', { params: { search: q, per_page: 10 } });
          f[which === 'bag' ? 'bagSuggestions' : 'productSuggestions'] = data.data || data;
        } catch (e) {}
      }, 300);
    },

    pickPackProduct(which, p) {
      const f = this.packForm;
      // Packaging items build a kit (bottle + cap + label, or one empty bag).
      if (which === 'bag') { if (!f.kit.some(k => k.product_id === p.id)) f.kit.push({ product_id: p.id, name: p.product, quantity: 1 }); f.bagSearch = ''; f.bagSuggestions = []; }
      else { f.product_id = p.id; f.productSearch = p.product; f.productSuggestions = []; }
    },

    async addPackSize() {
      const f = this.packForm;
      f.error = null;
      try {
        await axios.post('/api/manufacturing/pack-sizes', {
          grouped_inventory_id: this.selectedBom.grouped_inventory_id, product_id: f.product_id,
          content_quantity: f.content_quantity, content_unit: this.selectedBom.batch_unit,
          components: f.kit.map(k => ({ product_id: k.product_id, quantity: Number(k.quantity) || 1 })),
        });
        this.packForm = { productSearch: '', productSuggestions: [], product_id: null, content_quantity: null, bagSearch: '', bagSuggestions: [], packaging_product_id: null, kit: [], error: null };
        this.selectedBom = (await axios.get(`/api/manufacturing/boms/${this.selectedBom.id}`)).data;
      } catch (e) { f.error = apiError(e); }
    },

    async removePackSize(ps) {
      try {
        await axios.delete(`/api/manufacturing/pack-sizes/${ps.id}`);
        this.selectedBom = (await axios.get(`/api/manufacturing/boms/${this.selectedBom.id}`)).data;
      } catch (e) { this.$toasts.push({ type: 'error', message: apiError(e) }); }
    },

    openPackingHistory() {
      this.$refs.packingHistoryModal.show();
      this.loadPackingHistory();
    },
    async loadPackingHistory() {
      this.packingHistory.loading = true;
      try {
        this.packingHistory.rows = (await axios.get('/api/manufacturing/repacks', { params: { limit: 50, search: this.packingHistory.search || undefined } })).data;
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: apiError(e) });
      } finally {
        this.packingHistory.loading = false;
      }
    },
    debouncePackingHistory() {
      clearTimeout(this.packingHistoryTimer);
      this.packingHistoryTimer = setTimeout(() => this.loadPackingHistory(), 350);
    },
    async reversePacking(r) {
      try {
        const { data } = await axios.post(`/api/manufacturing/repacks/${r.id}/reverse`, { reason: this.packingHistory.reason });
        this.$toasts.push({ type: 'success', message: data.message });
        this.packingHistory.reversing = null;
        this.loadPackingHistory();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: apiError(e) });
      }
    },

    /** One click: pack the bags planned on the order (e.g. 3 x 70 kg), then finish if nothing is left. */
    async packAsPlanned() {
      this.packingPlanned = true;
      try {
        const { data } = await axios.post('/api/manufacturing/repacks', {
          production_order_id: this.activeOrder.id,
          lines: (this.activeOrder.planned_packs || []).map(l => ({ pack_size_id: l.pack_size_id, quantity: l.quantity })),
          notes: 'Packed as planned',
        });
        this.$toasts.push({ type: 'success', message: data.message });
        await this.refreshActiveOrder();
        this.loadOrders && this.loadOrders();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: apiError(e) });
      } finally {
        this.packingPlanned = false;
      }
    },

    async deleteSelectedBom() {
      this.deletingBom = true;
      try {
        const { data } = await axios.delete(`/api/manufacturing/boms/${this.selectedBom.id}`);
        this.$toasts.push({ type: 'success', message: data.message });
        this.$refs.deleteBomModal.hide();
        this.$refs.viewBomModal.hide();
        this.loadBoms();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: apiError(e) });
      } finally {
        this.deletingBom = false;
      }
    },

        async bomAction(action) {
      try {
        const body = action === 'reject' ? { reason: 'Rejected by approver' } : {};
        const { data } = await axios.patch(`/api/manufacturing/boms/${this.selectedBom.id}/${action}`, body);
        this.selectedBom = (await axios.get(`/api/manufacturing/boms/${this.selectedBom.id}`)).data;
        this.$toasts.push({ type: 'success', message: data.message });
        this.loadBoms();
      } catch (e) {
        if (!(action === 'approve' && this.askSelfApprovalReason(e, 'bom'))) this.$toasts.push({ type: 'error', message: apiError(e) });
      }
    },

    /** Shortcuts from the guide: open a dialog, switch tab, or go to another page. */
    onGuideAction(action) {
      if (action === 'sampleData') { this.sampleData.error = null; return this.$refs.sampleModal.show(); }
      if (action === 'newBom') return this.openModal('newBom');
      if (action === 'newOrder') return this.openModal('newOrder');
      if (action.startsWith('tab:')) {
        this.closeOrderDetail && this.activeOrderId && this.closeOrderDetail();
        this.currentTab = action.slice(4);
        return;
      }
      if (action.startsWith('url:')) window.location.href = action.slice(4);
    },

    async loadSampleData() {
      this.sampleData.busy = true; this.sampleData.error = null;
      try {
        await axios.post('/api/manufacturing/sample-data', { kind: this.sampleData.kind });
        this.$set(this.sampleData.kinds, this.sampleData.kind, true);
        this.sampleData.loaded = true;
        this.$refs.sampleModal.hide();
        this.$toasts.push({ type: 'success', message: 'Sample data loaded. Look for items marked “(sample)”.' });
        this.loadDashboard(); this.loadBoms && this.loadBoms(); this.loadOrders && this.loadOrders();
      } catch (e) { this.sampleData.error = apiError(e); } finally { this.sampleData.busy = false; }
    },

    traceLot(lot) {
      this.$set(this.visited, 'trace', true);
      this.currentTab = 'trace';
      this.$nextTick(() => this.$refs.tracePanel && this.$refs.tracePanel.trace(lot));
    },

    async saveWastage() {
      if (!this.wastageForm.product_id || !this.wastageForm.quantity || !this.wastageForm.reason) {
        this.$toasts.push({ type: 'warning', message: 'Fill all required fields.' }); return;
      }
      try {
        await axios.post('/api/manufacturing/production-wastage', this.wastageForm);
        this.$toasts.push({ type: 'success', message: 'Wastage recorded.' });
        this.refreshActiveOrder();
      } catch (e) { this.$toasts.push({ type: 'danger', message: e.response?.data?.message || 'Failed.' }); }
    },

    onBomSaved(saved) {
      const wasEdit = !!this.editingBom;
      this.$refs.newBomModal.hide();
      this.loadBoms();
      if (wasEdit && saved && saved.id) {
        this.$toasts.push({ type: 'success', message: `Saved as v${saved.version} (draft). Submit it for approval to use it in production.` });
        this.openModal('viewBom', saved); // straight to the new version, where Submit for approval is
      } else {
        this.$toasts.push({ type: 'success', message: 'BOM saved.' });
      }
    },

    resetBomForm() {
      this.editBomInPlace = false;
      this.editingBom = null;
      this.$refs.bomFormRef?.reset?.();
    },

    // ── Helpers ───────────────────────────────────────────────────────────────

    batchBarPct(b) { return Math.max(5, Math.round((b.actual_cost / this.maxBatchCost) * 100)); },
    statusClass(s) {
      const m = { draft: 'status-draft', pending_approval: 'status-pending', approved: 'status-approved', in_progress: 'status-inprogress', paused: 'status-pending', completed: 'status-completed', cancelled: 'status-cancelled', reversed: 'status-cancelled' };
      return m[s] || 'status-draft';
    },
    formatStatus(s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()); },
    /** quantity x current buying price, converting kg/g and l/ml; null when it can't be valued. */
    unitFactorBetween(from, to) {
      const f = (from || '').trim().toLowerCase(), t = (to || '').trim().toLowerCase();
      if (f === t) return 1;
      const tables = [{ kg: 1000, g: 1, mg: 0.001 }, { l: 1000, ltr: 1000, litre: 1000, liter: 1000, ml: 1 }];
      const tbl = tables.find(x => x[f] !== undefined && x[t] !== undefined);
      return tbl ? tbl[f] / tbl[t] : null;
    },
    bomLineValue(m) {
      // Valued on the server with the real unit conversions (see BomController@show).
      if (m.line_value !== undefined) return m.line_value;
      const p = m.product || {};
      if (p.wholesale_ppu === null || p.wholesale_ppu === undefined || p.wholesale_ppu === '') return null;
      const f = (m.unit || '').trim().toLowerCase(), t = (p.measuring_unit || m.unit || '').trim().toLowerCase();
      const tables = [{ kg: 1000, g: 1, mg: 0.001 }, { l: 1000, ltr: 1000, litre: 1000, liter: 1000, ml: 1 }];
      const tbl = f === t ? null : tables.find(x => x[f] !== undefined && x[t] !== undefined);
      const factor = f === t ? 1 : (tbl ? tbl[f] / tbl[t] : null);
      return factor === null ? null : Number(m.quantity || 0) * factor * Number(p.wholesale_ppu);
    },
    orderStatusLabel(o) {
      if (o && o.status === 'completed' && ['awaiting', 'in_progress'].includes(o.packing_status)) return 'Packing';
      return this.formatStatus(o && o.status);
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; },
    relativeTime(d) {
      if (!d) return '';
      const diff = Date.now() - new Date(d);
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return `${Math.floor(mins / 1440)}d ago`;
    },
    stageDuration(stage) {
      if (!stage.started_at || !stage.completed_at) return '';
      const mins = Math.round((new Date(stage.completed_at) - new Date(stage.started_at)) / 60000);
      return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
    },
    formatCurrency(v) {
      if (v == null) return '—';
      return typeof window.convertCurrencyToLocal === 'function'
        ? window.convertCurrencyToLocal(v)
        : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 });
    },
    defaultFrom() {
      const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
    },

    // ── Stock Ledger modal ────────────────────────────────────────────────────

    onStockLedgerShown() {
      // Focus the search input when modal opens
      this.$nextTick(() => {
        const el = this.$el.querySelector('[data-ledger-search]');
        if (el) el.focus();
      });
    },

    async searchLedgerProduct() {
      const q = (this.stockLedger.productSearch || '').toLowerCase();
      if (q.length < 2) { this.stockLedger.suggestions = []; return; }
      clearTimeout(this.stockLedger._timer);
      this.stockLedger._timer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/products', { params: { search: q, per_page: 10 } });
          this.stockLedger.suggestions = data.data || data;
        } catch (e) {}
      }, 300);
    },

    selectLedgerProduct(p) {
      this.stockLedger.selectedProduct = p;
      this.stockLedger.productSearch   = p.product;
      this.stockLedger.suggestions     = [];
      this.loadStockLedger();
    },

    async loadStockLedger(page) {
      if (!this.stockLedger.selectedProduct) return;
      this.stockLedger.loading = true;
      try {
        const { data } = await axios.get(
          `/api/manufacturing/stock-ledger/product/${this.stockLedger.selectedProduct.id}`,
          { params: { entry_type: this.stockLedger.entryType, from: this.stockLedger.from, to: this.stockLedger.to, page: page || 1, per_page: this.stockLedger.perPage } }
        );
        this.stockLedger.entries     = data.data;
        this.stockLedger.total       = data.total;
        this.stockLedger.currentPage = data.current_page;
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Failed to load ledger.' });
      } finally {
        this.stockLedger.loading = false;
      }
    },

    ledgerEntryBadge(t) {
      const m = { purchase: 'badge-primary', production_consumption: 'badge-secondary', production_output: 'badge-primary', sale: 'badge-secondary', wastage: 'badge-danger', adjustment: 'badge-secondary', transfer_in: 'badge-primary', transfer_out: 'badge-secondary' };
      return 'badge ' + (m[t] || 'badge-secondary');
    },

    formatEntryType(t) {
      return (t || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    clearStockLedger() {
      this.stockLedger.selectedProduct = null;
      this.stockLedger.productSearch   = '';
      this.stockLedger.entries         = [];
      this.stockLedger.total           = 0;
    },
  },

  watch: {
    currentTab(tab) {
      if (!this.canViewRecipes && RECIPE_TABS.includes(tab)) { this.currentTab = 'dashboard'; return; }
      this.$set(this.visited, tab, true);
      if (tab === 'orders' && !this.orders.length) this.loadOrders();
      if (tab === 'boms'   && !this.boms.length)   this.loadBoms();
    },
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────── */
.mfg-app {
  position: relative;
  min-height: 100vh;
  padding-bottom: 72px; /* space for bottom nav */
}
.mfg-content {
  padding: 0 0 16px;
}

/* ── Bottom nav ──────────────────────────────────────────────────── */
.mfg-nav {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 100;
  display: flex;
  background: #fff;
  border-top: 1px solid #e9ecef;
  padding: 4px 0 env(safe-area-inset-bottom, 4px);
  box-shadow: 0 -2px 12px rgba(0,0,0,.07);
}
.mfg-nav-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  padding: 6px 0;
  color: #6c757d;
  font-size: 11px;
  cursor: pointer;
  transition: color .15s;
}
.mfg-nav-btn i { font-size: 20px; }
.mfg-nav-btn.active { color: #fd4719; }
.mfg-nav-btn.active i { color: #fd4719; }

/* ── Header ──────────────────────────────────────────────────────── */
.mfg-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px;
}

.mfg-subtitle { font-size: 12px; color: #6c757d; margin: 0; }

/* ── KPI strip ───────────────────────────────────────────────────── */
.kpi-strip {
  display: flex;
  gap: 8px;
  padding: 0 16px 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.kpi-strip::-webkit-scrollbar { display: none; }
.kpi-card {
  flex: 0 0 auto;
  min-width: 90px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 10px 12px;
  text-align: center;
  cursor: pointer;
  transition: background .15s;
}
.kpi-card:hover { background: #e9ecef; }
.kpi-card.skeleton { background: #e9ecef; min-height: 64px; animation: pulse 1.2s ease infinite; }
.kpi-value { font-size: 1.1rem; font-weight: 700; }
.kpi-label { font-size: 10px; color: #6c757d; }
.kpi-delta { font-size: 10px; margin-top: 2px; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

/* ── Alert banner ────────────────────────────────────────────────── */
.alert-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 8px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #e9ecef; border-left: 3px solid #fd4719;
}
.alert-banner--danger { border-left-color: #dc3545; }

/* ── Section headers ─────────────────────────────────────────────── */
.section-header { padding: 0 !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px 6px;
}
.section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #6c757d; margin: 0; }
.btn-link-sm { background: none; border: none; color: #0f172a; font-weight: 600; font-size: 12px; padding: 0; cursor: pointer; }

/* ── Order cards ─────────────────────────────────────────────────── */
.order-cards { display: flex; flex-direction: column; }
.order-card { background: transparent; border: 0; border-top: 1px solid #f1f5f9; border-radius: 0; padding: 12px 16px; cursor: pointer; }
.order-card:first-child { border-top: 0; }

.order-card:hover { background: #f8fafc; }
.order-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.order-number { font-size: 13px; font-weight: 600; color: #0f172a; margin-right: 6px; }
.order-time { font-size: 11px; color: #6c757d; }
.order-product { font-size: 14px; font-weight: 500; margin-bottom: 6px; }
.order-card-bottom { display: flex; align-items: center; gap: 10px; }
.order-meta { font-size: 12px; color: #6c757d; }
.progress-dots { display: flex; gap: 4px; margin-left: auto; }
.progress-dot { width: 8px; height: 8px; border-radius: 50%; background: #dee2e6; }
.progress-dot.completed { background: #fd4719; }
.progress-dot.in_progress { background: #0f172a; }
.progress-dot.pending { background: #dee2e6; }

/* ── Status pills ────────────────────────────────────────────────── */
.status-pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.status-pill--lg { font-size: 12px; padding: 4px 10px; }
.status-draft { background: #f1f5f9; color: #64748b; }
.status-pending { background: #fff1ec; color: #c2410c; }
.status-approved { background: #e2e8f0; color: #0f172a; }
.status-inprogress { background: #0f172a; color: #fff; }
.status-completed { background: #fd4719; color: #fff; }
.status-cancelled { background: #fdecea; color: #dc3545; }

/* ── Batch bar chart ─────────────────────────────────────────────── */
.batch-list { padding: 0 16px; display: flex; flex-direction: column; gap: 6px; }
.batch-row { display: flex; flex-direction: column; gap: 2px; }
.batch-bar-wrap { background: #f0f0f0; border-radius: 4px; height: 6px; overflow: hidden; }
.batch-bar { height: 100%; border-radius: 4px; transition: width .4s; }
.bar-good { background: #fd4719; }
.bar-warn { background: #94a3b8; }
.bar-bad  { background: #dc3545; }
.batch-info { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.batch-name { flex: 1; color: #495057; }
.batch-cost { color: #6c757d; }
.batch-eff  { font-weight: 600; }

/* ── Filter bar ──────────────────────────────────────────────────── */


/* ── Empty state ─────────────────────────────────────────────────── */
.empty-state { text-align: center; padding: 32px 16px; }

/* ── Slide-over panel ────────────────────────────────────────────── */
.slide-over-panel {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 100%;
  max-width: 560px;
  z-index: 200;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 32px rgba(0,0,0,.15);
  overflow: hidden;
}
.slide-over-backdrop {
  position: fixed;
  inset: 0;
  z-index: 199;
  background: rgba(0,0,0,.35);
}
.slide-over-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}
.slide-over-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  -webkit-overflow-scrolling: touch;
}
.btn-back {
  background: none; border: none; padding: 0; font-size: 18px; cursor: pointer; color: #0f172a;
}
.btn-icon { background: none; border: none; padding: 0; font-size: 16px; cursor: pointer; color: #6c757d; }

/* ── Slide-over content ──────────────────────────────────────────── */
.info-pills { display: flex; flex-wrap: wrap; gap: 8px; }
.info-pill { padding: 6px 14px 6px 0; margin-right: 6px; border-right: 1px solid #eef0f2; }
.info-pill-label { font-size: 10px; color: #6c757d; display: block; text-transform: uppercase; letter-spacing: .04em; }
.info-pill-value { font-size: 14px; font-weight: 600; }

.stage-row { display: flex; gap: 12px; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
.stage-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; background: #dee2e6; }
.stage-dot--in_progress { background: #0f172a; animation: pulse 1.5s ease infinite; }
.stage-dot--completed { background: #fd4719; }
.stage-dot--skipped { background: #adb5bd; }
.stage-body { flex: 1; }
.stage-name { font-size: 14px; font-weight: 500; }
.stage-meta { font-size: 11px; color: #6c757d; margin-top: 2px; }

.material-table { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.material-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
.material-row:last-child { border-bottom: none; }
.material-row--short { background: #fff5f5; }
.material-row--warn  { background: #f8fafc; }
.material-name { flex: 1; font-weight: 500; }
.material-qty  { color: #6c757d; }
.material-cost { min-width: 60px; text-align: right; }

.wastage-row { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 4px 0; }
.order-notes { background: #f8f9fa; border-radius: 8px; padding: 10px 12px; font-size: 13px; color: #495057; margin-bottom: 12px; }
.order-footer-meta { font-size: 11px; color: #adb5bd; padding-top: 8px; border-top: 1px solid #f0f0f0; }

/* ── BOM stage preview ───────────────────────────────────────────── */
.bom-stage-preview { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.bom-stage-header { background: #f8f9fa; padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #e9ecef; }

/* ── Availability widget ─────────────────────────────────────────── */
.availability-widget { background: #f8f9fa; border-radius: 10px; padding: 12px; margin-bottom: 12px; }

/* ── Typeahead ───────────────────────────────────────────────────── */
.typeahead-drop {
  position: absolute; left: 0; right: 0; z-index: 1000;
  background: #fff; border: 1px solid #dee2e6; border-radius: 8px;
  max-height: 200px; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,.1);
}
.typeahead-item { display: block; width: 100%; text-align: left; padding: 8px 12px; border: none; background: none; cursor: pointer; font-size: 13px; }
.typeahead-item:hover { background: #f0f4ff; }
.typeahead-group { padding: 6px 12px 2px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6c757d; }

/* ── Transitions ─────────────────────────────────────────────────── */
.slide-over-enter-active, .slide-over-leave-active { transition: transform .28s cubic-bezier(.4,0,.2,1); }
.slide-over-enter, .slide-over-leave-to { transform: translateX(100%); }
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── No-BOM prompt states ────────────────────────────────────────── */
.no-bom-prompt {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}
.no-bom-prompt--empty {
  background: #fff7ed;
  border-color: #fed7aa;
}
.no-bom-icon {
  font-size: 1.6rem;
  flex-shrink: 0;
  color: #3b82f6;
  margin-top: 2px;
}
.no-bom-prompt--empty .no-bom-icon { color: #f97316; }
.no-bom-body { flex: 1; }
.no-bom-body p { font-size: 13px; margin-bottom: 0; }

/* ── Ingredient preview pills ────────────────────────────────────── */
.no-bom-ingredients { display: flex; flex-wrap: wrap; gap: 6px; }
.ingredient-pill {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  color: #374151;
}
.ingredient-pill--more {
  background: #f1f5f9;
  color: #64748b;
}

/* ── Confirmed BOM pill ──────────────────────────────────────────── */
.bom-confirmed-pill {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  flex-wrap: wrap;
  gap: 4px;
}

/* ── Form label small ────────────────────────────────────────────── */
.form-label-sm {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  display: block;
}

/* ── btn-xs ──────────────────────────────────────────────────────── */
.btn-xs { padding: 2px 8px; font-size: 11px; border-radius: 6px; }

/* ── More / Tools tab grid ───────────────────────────────────────── */
.more-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); background: #fff; border: 1px solid #e9ecef; border-radius: 14px; overflow: hidden; }
.more-card { position: relative; display: flex; flex-direction: column; gap: 2px; padding: 18px 20px; cursor: pointer; border-right: 1px solid #eef0f2; border-bottom: 1px solid #eef0f2; }
.more-card:hover  { background: #f8fafc; }

.more-card-icon  { font-size: 1.1rem; margin-bottom: 4px; color: #64748b !important; }
.more-card-label { font-size: 14px; font-weight: 600; color: #0f172a; }
.more-card-sub   { font-size: 11px; color: #6c757d; line-height: 1.4; }
.more-card-badge { position: absolute; top: 10px; right: 10px; }

/* ── Shared typeahead (used inside modals and BomFormInline) ─────── */
.bom-typeahead {
  position: absolute; left: 0; right: 0; z-index: 2000;
  background: #fff; border: 1px solid #dee2e6; border-radius: 8px;
  max-height: 200px; overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0,0,0,.12);
  margin-top: 2px;
}
.bom-typeahead-item {
  display: flex; align-items: center; width: 100%; text-align: left;
  padding: 8px 12px; border: none; background: none;
  cursor: pointer; font-size: 13px; gap: 8px;
}
.bom-typeahead-item:hover { background: #f0f4ff; }

@media (min-width: 768px) {
  .slide-over-panel { width: 480px; }
  .more-grid { grid-template-columns: repeat(3, 1fr); }
}
</style>

<style>
/* Shared look for the manufacturing hub and its panels — matches the
   recent pages (Projects, Cash Shift Control, Invoices overview). */
.mfg-app { background: #f8fafc; min-height: 100vh; }
.mfg-app .mfg-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 1.25rem; padding: 0; }
.mfg-app .mfg-title { color: #fd4719; font-size: 22px; font-weight: 600; letter-spacing: -0.3px; margin: 0; }
.mfg-app .mfg-subtitle { font-size: 12px; color: #64748b; margin: 0; }
.mfg-app .section-title { font-size: 14px; font-weight: 600; color: #0f172a; text-transform: none; letter-spacing: 0; margin: 1.25rem 0 .5rem; }
.mfg-app .filter-bar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 1rem; padding: 0; }
.mfg-app .mfg-tabs { display: flex; gap: 22px; border-bottom: 1px solid #e9ecef; overflow-x: auto; white-space: nowrap; }
.mfg-app .mfg-tab { padding: 10px 0; margin-bottom: -1px; font-size: 13px; font-weight: 500; color: #64748b; border-bottom: 2px solid transparent; text-decoration: none; }
.mfg-app .mfg-tab:hover { color: #0f172a; text-decoration: none; }
.mfg-app .mfg-tab.active { color: #0f172a; font-weight: 600; border-bottom-color: #fd4719; }
.mfg-app .mfg-kpis .kpi-col { border-right: 1px solid #eef0f2; margin-bottom: .75rem; }
.mfg-app .mfg-kpis .kpi-col:last-child { border-right: none; }
@media (max-width: 767px) { .mfg-app .mfg-kpis .kpi-col { border-right: none; } }
.mfg-app .mfg-box { background: #fff; border: 1px solid #e9ecef; border-radius: 14px; overflow: hidden; }
.mfg-app .mfg-box > .card-body, .mfg-app .mfg-box.mb-3 > .card-body { padding: 20px; }
.mfg-app .mfg-box .table { margin-bottom: 0; }
.mfg-app .mfg-box .table thead th { border-top: 0; font-size: 12px; font-weight: 500; color: #64748b; background: #fff; }
.mfg-app .table td { font-size: 13px; }
.mfg-app .mfg-guide-btn { flex-shrink: 0; width: 34px; height: 34px; margin-bottom: 4px; border: 1px solid #e9ecef; border-radius: 50%; background: #fff; color: #64748b; font-size: 16px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
.mfg-app .mfg-guide-btn:hover { color: #fd4719; border-color: #fd4719; }
</style>
